<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import { getVisualViewport, layoutPx } from 'dashboard/helper/dashboardZoom';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioPresentation from 'dashboard/components-next/message/bubbles/AudioPresentation.vue';
import {
  getAudioAppearance,
  getAudioAvatarIdentity,
  isForwardedAudio,
} from 'dashboard/components-next/message/bubbles/audioIdentity';
// Aceleres: a figurinha vetorial (Lottie) nao tem imagem — quem a desenha e o
// mesmo player do balao e da citacao, para o modal de informacoes mostrar
// exatamente o que a conversa mostra.
import LottieStickerCanvas from 'dashboard/components-next/message/bubbles/LottieStickerCanvas.vue';
import { isLottieStickerAttachment } from 'dashboard/helper/lottieStickerPlayer';
// Aceleres 2026-08-17: a mesma pergunta que Message.vue e bubbles/Video.vue
// fazem para escolher a bolha. O GIF do WhatsApp chega como video mudo
// (`file_type: 'video'`), entao so este helper o distingue de um video
// normal — e reusa-lo e o que impede o modal de divergir do chat.
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
// Aceleres 2026-08-17: a MESMA regua de tamanho do GIF da conversa
// (bubbles/Video.vue e Image.vue). O tamanho do GIF nao pode sair da
// largura da bolha: a bolha e `width: fit-content`, entao ela media o
// texto e o GIF ficava do tamanho da legenda -- grande quando nao havia
// texto, pequeno quando havia.
import {
  gifFrameStyle,
  photoDisplayBox,
} from 'dashboard/components-next/message/bubbles/mediaAspectLayout';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
// Aceleres: a citacao do modal sai do mesmo helper que o balao e o card do
// compositor usam — autor, cor, rotulo por tipo e miniatura vem todos dali,
// para as tres telas nunca divergirem.
import { describeQuotedMessage } from 'dashboard/helper/quotedMessagePreview';
import QuotedMediaIcon from 'dashboard/components-next/message/QuotedMediaIcon.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { createPdfFirstPagePreviewFromUrl } from 'dashboard/helper/pdfPreviewHelper';
import MessageReactionBar from '../../../components/widgets/conversation/MessageReactionBar.vue';
import WhatsAppEmojiPicker from '../../../components/widgets/conversation/WhatsAppEmojiPicker.vue';
import AceleresMessageApi from 'dashboard/api/aceleres/messageActions';
import ForwardMessageModal from './ForwardMessageModal.vue';
import PinDurationDialog from 'dashboard/components-next/message/PinDurationDialog.vue';
import { emitter } from 'shared/helpers/mitt';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { provide, ref } from 'vue';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import {
  getMobileActionsTop,
  isMobileMessageMenu,
} from 'dashboard/components-next/message/contextMenuAnchor.js';

// Aceleres 2026-08-25: era a terceira copia da aritmetica de zoom do painel.
// A regra unica vive agora em helper/dashboardZoom.js, que explica os dois
// espacos de coordenadas (visual x layout) e porque menus nasciam longe do rato.
const fixedPosition = layoutPx;

function getVisualViewportBounds() {
  const rootRect = document.documentElement.getBoundingClientRect();
  const viewport = getVisualViewport();

  // `rootRect` ja vem em espaco visual, tal como `innerWidth`; ficou porque
  // tambem entrega o `left`/`top` da raiz, que o fallback nao tem.
  return {
    zoom: viewport.zoom,
    left: rootRect.left,
    top: rootRect.top,
    width: rootRect.width || viewport.width,
    height: rootRect.height || viewport.height,
  };
}

function getUntransformedPopupSize(root, rect) {
  const transform = getComputedStyle(root).transform;
  const matrix = transform.match(/^matrix\(([^)]+)\)$/);
  const values = matrix?.[1]?.split(',').map(Number);
  const scaleX = Math.abs(values?.[0]) || 1;
  const scaleY = Math.abs(values?.[3]) || 1;

  return {
    width: rect.width / scaleX,
    height: rect.height / scaleY,
  };
}

function decodeAttachmentName(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// Aceleres: Attachment#file_metadata nao envia file_name nem fallback_title,
// entao o nome real do documento vem do ultimo segmento da data_url do
// ActiveStorage — mesma estrategia do bubble do chat (bubbles/File.vue).
// Aceleres 2026-08-17: proporcao do anexo a partir das dimensoes do payload,
// nas duas grafias (o anexo chega ora cru do store, ora ja transformado).
// Teto de altura da midia com moldura (GIF e foto) na gaveta de informacoes.
// Menor que os 420px da conversa de proposito: a gaveta tem 460px de largura e
// a lista de status vem logo abaixo da previa, entao uma midia em pe com a
// altura da conversa empurraria tudo para fora da tela.
const MESSAGE_INFO_MEDIA_MAX_HEIGHT = 320;
// Largura util da previa, derivada da gaveta em vez de escolhida: 460px de
// gaveta, menos 24px de respiro de cada lado do fundo quadriculado, vezes os
// 92% de teto da bolha, menos os 12px de preenchimento de cada lado dela.
const MESSAGE_INFO_MEDIA_MAX_WIDTH = Math.floor((460 - 24 * 2) * 0.92) - 12 * 2;
// Aceleres 2026-08-18: mesma regua de texto longo do balao da conversa
// (components-next/message/bubbles/Text/Index.vue): corta em blocos de 770
// caracteres com "..." e um "Ler mais"; cada clique revela mais um bloco, e o
// botao reaparece enquanto sobrar texto. Sem isto a gaveta crescia sem fim e
// empurrava a lista de status (Enviada/Entregue/Lida) para fora da tela.
const MESSAGE_INFO_TEXT_TRUNCATE_LENGTH = 770;

function attachmentAspectRatio(attachment) {
  const width = Number(attachment?.width);
  const height = Number(attachment?.height);

  return width > 0 && height > 0 ? width / height : null;
}

function attachmentNameFromUrl(url) {
  const cleanUrl = String(url || '').split(/[?#]/, 1)[0];
  const name = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
  return decodeAttachmentName(name);
}

function formatAttachmentSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1
  );
  const value = size / 1024 ** unitIndex;
  const rounded =
    unitIndex === 0 || value >= 10 ? Math.round(value) : value.toFixed(1);

  return `${rounded} ${units[unitIndex]}`;
}

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    AudioPresentation,
    LottieStickerCanvas,
    QuotedMediaIcon,
    FileIcon,
    MessageReactionBar,
    WhatsAppEmojiPicker,
    ForwardMessageModal,
    PinDurationDialog,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    // Aceleres: um álbum de imagens chega como uma única bolha visual, mas
    // continua sendo composto por várias mensagens reais. As ações do menu
    // devem receber todas elas para não operar apenas sobre a última.
    selectionMessages: {
      type: Array,
      default: () => [],
    },
    // Aceleres: a mensagem citada, ja resolvida pelo MessageList. O modal de
    // informacoes precisa dela para mostrar o contexto da resposta, como o
    // balao da conversa mostra.
    inReplyTo: {
      type: Object,
      default: null,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    // Aceleres: quando true, ao abrir o popup já entra direto no picker full
    // (usado pelo "+" da hover-pill de reação rápida em Message.vue).
    initialShowPicker: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'open',
    'close',
    'replyTo',
    'react',
    'openQuickReact',
  ],
  setup() {
    // Aceleres: sobrescreve o `contextMenuElementTarget` provido pelo
    // MessagesView para que o useScrollLock do shared ContextMenu vire
    // no-op — senão o scroll do painel inteiro some e o conteudo shifta
    // de largura quando o menu abre.
    provide('contextMenuElementTarget', ref(null));
    const { getPlainText } = useMessageFormatter();
    const {
      isSelectionMode,
      enterSelectionMode,
      exitSelectionMode,
      isMessageSelected,
      toggleMessageSelection,
    } = useMessageSelection();
    return {
      getPlainText,
      isSelectionMode,
      enterSelectionMode,
      exitSelectionMode,
      isMessageSelected,
      toggleMessageSelection,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showEditModal: false,
      editAnchorRect: null,
      editPopupStyle: null,
      editContent: '',
      editOriginalContent: '',
      editSubmitting: false,
      showEditEmojiPicker: false,
      editEmojiPickerStyle: null,
      showForwardModal: false,
      showEmojiPicker: false,
      showMessageInfoModal: false,
      messageInfoPdfPreviews: {},
      // Proporcao real de cada GIF, lida do <video> no `loadedmetadata`.
      messageInfoGifRatios: {},
      // Aceleres 2026-08-17: naturalWidth/naturalHeight de cada foto ja
      // carregada, por chave de anexo. Mesmo papel que `messageInfoGifRatios`
      // faz para o GIF.
      messageInfoImageSizes: {},
      // Marca d'agua de quanto do texto ja foi revelado na previa da gaveta;
      // comeca em um bloco e cresce um bloco por clique em "Ler mais".
      messageInfoTextVisibleLength: MESSAGE_INFO_TEXT_TRUNCATE_LENGTH,
      messageInfoPdfLoading: {},
      resolvedContextMenuPosition: {},
      aceleresOutsideHandler: null,
      aceleresScrollHandler: null,
      aceleresKeyHandler: null,
      aceleresViewportHandler: null,
      editOutsideHandler: null,
      editScrollHandler: null,
      editResizeHandler: null,
      // Aceleres 2026-09-09: no telemovel o menu de opcoes deixa de ter
      // "Reagir" -- a barra de reacoes rapidas ja esta ali por cima, e o
      // seu "+" continua a abrir o seletor completo, portanto a opcao era
      // um segundo caminho para o mesmo sitio. Fica um `data` em vez de um
      // `computed` de proposito: um computed sem dependencias reativas
      // guardava para sempre o valor da primeira leitura e nao acompanhava
      // uma rotacao do aparelho. Aqui e relido a cada abertura do popup.
      isMobileViewport: false,
    };
  },
  watch: {
    contextMenuPosition: {
      deep: true,
      immediate: true,
      handler(position) {
        this.resolvedContextMenuPosition = { ...(position || {}) };
        if (this.isOpen) this.$nextTick(this.clampPopupToViewport);
      },
    },
    isOpen(open) {
      if (open) {
        this.isMobileViewport = isMobileMessageMenu();
        this.showEditModal = false;
        this.editSubmitting = false;
        this.closeEditEmojiPicker();
        // Toda abertura do popup reseta estados internos — se o picker
        // grande estava aberto na última vez, esquece e volta ao menu normal.
        // EXCEÇÃO: se `initialShowPicker` veio true (chamado pelo "+" da
        // hover-pill em Message.vue), entra direto no picker.
        // Aceleres 2026-09-15: no telemovel o "+" das figurinhas abre a folha
        // grande de emojis em vez do cartao.
        if (this.initialShowPicker && this.isMobileViewport) {
          this.openMobileReactionSheet();
          return;
        }
        this.showEmojiPicker = !!this.initialShowPicker;
        if (this.showEmojiPicker) {
          emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
            owner: 'message',
            messageId: this.messageId,
          });
        }
        this.resolvedContextMenuPosition = {
          ...(this.contextMenuPosition || {}),
        };
        // Click-outside fecha o popup. setTimeout pra não pegar o próprio
        // clique do chevron que abriu (que ainda está propagando).
        setTimeout(() => {
          if (!this.isOpen) return;
          this.aceleresOutsideHandler = e => {
            const root = this.$refs.aceleresPopupRoot;
            if (root && !root.contains(e.target)) {
              this.handleClose();
            }
          };
          document.addEventListener(
            'pointerdown',
            this.aceleresOutsideHandler,
            true
          );
        }, 0);
        // Scroll em QUALQUER ancestor (capture:true pega o painel da
        // conversa) fecha — popup é position:fixed, se a mensagem rola,
        // o popup ficaria pendurado no ar; melhor fechar.
        // EXCEÇÃO: scroll DENTRO do próprio popup (ex: rolar a lista de
        // emojis no picker) NÃO fecha — verifica se o target tá contido.
        this.aceleresScrollHandler = e => {
          const root = this.$refs.aceleresPopupRoot;
          if (root && e.target && root.contains(e.target)) return;
          this.handleClose();
        };
        window.addEventListener('scroll', this.aceleresScrollHandler, {
          capture: true,
          passive: true,
        });
        this.aceleresKeyHandler = event => {
          if (event.key === 'Escape') this.handleClose();
        };
        document.addEventListener('keydown', this.aceleresKeyHandler);
        this.aceleresViewportHandler = () => this.handleClose();
        window.addEventListener('resize', this.aceleresViewportHandler, {
          passive: true,
        });
        window.visualViewport?.addEventListener(
          'resize',
          this.aceleresViewportHandler,
          { passive: true }
        );
        window.visualViewport?.addEventListener(
          'scroll',
          this.aceleresViewportHandler,
          { passive: true }
        );
        this.$nextTick(() => {
          this.clampPopupToViewport();
          this.focusPopupForKeyboard();
        });
      } else {
        if (this.aceleresOutsideHandler) {
          document.removeEventListener(
            'pointerdown',
            this.aceleresOutsideHandler,
            true
          );
          this.aceleresOutsideHandler = null;
        }
        if (this.aceleresScrollHandler) {
          window.removeEventListener(
            'scroll',
            this.aceleresScrollHandler,
            true
          );
          this.aceleresScrollHandler = null;
        }
        if (this.aceleresKeyHandler) {
          document.removeEventListener('keydown', this.aceleresKeyHandler);
          this.aceleresKeyHandler = null;
        }
        if (this.aceleresViewportHandler) {
          window.removeEventListener('resize', this.aceleresViewportHandler);
          window.visualViewport?.removeEventListener(
            'resize',
            this.aceleresViewportHandler
          );
          window.visualViewport?.removeEventListener(
            'scroll',
            this.aceleresViewportHandler
          );
          this.aceleresViewportHandler = null;
        }
        this.showEditModal = false;
        this.editSubmitting = false;
        this.closeEditEmojiPicker();
      }
    },
    showEditModal(open) {
      if (open) {
        this.$nextTick(() => {
          const ta = this.$refs.aceleresEditTextarea;
          if (ta && typeof ta.focus === 'function') {
            ta.focus();
            try {
              ta.setSelectionRange(
                this.editContent.length,
                this.editContent.length
              );
            } catch (_) {
              /* ignore */
            }
          }
        });
      } else {
        this.closeEditEmojiPicker();
      }
    },
    showEmojiPicker() {
      this.$nextTick(this.clampPopupToViewport);
    },
    initialShowPicker(showPicker) {
      if (!this.isOpen) return;
      this.showEmojiPicker = showPicker;
      if (showPicker) {
        emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
          owner: 'message',
          messageId: this.messageId,
        });
      }
    },
  },
  mounted() {
    emitter.on(BUS_EVENTS.EMOJI_PICKER_OPENED, this.onEmojiPickerOpened);
  },
  beforeUnmount() {
    if (this.aceleresOutsideHandler) {
      document.removeEventListener(
        'pointerdown',
        this.aceleresOutsideHandler,
        true
      );
    }
    if (this.aceleresScrollHandler) {
      window.removeEventListener('scroll', this.aceleresScrollHandler, true);
    }
    if (this.aceleresKeyHandler) {
      document.removeEventListener('keydown', this.aceleresKeyHandler);
    }
    if (this.aceleresViewportHandler) {
      window.removeEventListener('resize', this.aceleresViewportHandler);
      window.visualViewport?.removeEventListener(
        'resize',
        this.aceleresViewportHandler
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        this.aceleresViewportHandler
      );
    }
    document.removeEventListener('keydown', this.handleMessageInfoEscape);
    // Aceleres: garante que o listener em capture do picker de emoji do modal
    // de editar não fique pendurado se o componente sair fora do fluxo normal.
    document.removeEventListener(
      'mousedown',
      this.handleEditEmojiOutsideClick,
      true
    );
    emitter.off(BUS_EVENTS.EMOJI_PICKER_OPENED, this.onEmojiPickerOpened);
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    mobileFocusStyle() {
      const viewport = getVisualViewport();
      const focusedCenter = Number(
        this.resolvedContextMenuPosition.focusedCenter
      );
      const bubbleHeight = Number(
        this.resolvedContextMenuPosition.bubbleHeight
      );
      const bubbleLeft = Number(this.resolvedContextMenuPosition.bubbleLeft);
      const bubbleRight = Number(this.resolvedContextMenuPosition.bubbleRight);

      return {
        '--aceleres-focused-center-y': fixedPosition(
          Number.isFinite(focusedCenter)
            ? focusedCenter
            : viewport.height * 0.36,
          viewport.zoom
        ),
        '--aceleres-focused-bubble-height': fixedPosition(
          Number.isFinite(bubbleHeight) && bubbleHeight > 0 ? bubbleHeight : 72,
          viewport.zoom
        ),
        '--aceleres-focused-left': fixedPosition(
          Number.isFinite(bubbleLeft) ? Math.max(12, bubbleLeft) : 12,
          viewport.zoom
        ),
        '--aceleres-focused-right': fixedPosition(
          Number.isFinite(bubbleRight)
            ? Math.max(12, viewport.width - bubbleRight)
            : 12,
          viewport.zoom
        ),
        '--aceleres-mobile-actions-top': Number.isFinite(
          Number(this.resolvedContextMenuPosition.mobileActionsTop)
        )
          ? fixedPosition(
              Number(this.resolvedContextMenuPosition.mobileActionsTop),
              viewport.zoom
            )
          : undefined,
      };
    },
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    messageInfoHasMoreText() {
      return (
        (this.plainTextContent || '').length > this.messageInfoTextVisibleLength
      );
    },
    messageInfoVisibleText() {
      const texto = this.plainTextContent || '';
      if (texto.length <= this.messageInfoTextVisibleLength) return texto;

      return `${texto.slice(0, this.messageInfoTextVisibleLength)}...`;
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    actionMessages() {
      return this.selectionMessages.length
        ? this.selectionMessages
        : [this.message];
    },
    isMessageGroup() {
      return this.actionMessages.length > 1;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    isPinned() {
      return !!(this.enabledOptions && this.enabledOptions.pinned);
    },
    isStarred() {
      const starred = this.getUISettings?.starred_message_ids || [];
      return starred.some(id => Number(id) === Number(this.messageId));
    },
    pinMenuOption() {
      return {
        icon: this.isPinned ? 'i-lucide-pin-off' : 'i-lucide-pin',
        label: this.isPinned ? 'Desafixar' : 'Fixar mensagem',
      };
    },
    starMenuOption() {
      return {
        icon: this.isStarred ? 'i-lucide-star-off' : 'i-lucide-star',
        label: this.isStarred ? 'Remover' : 'Favoritar',
      };
    },
    isFailedMessage() {
      return this.message.status === 'failed';
    },
    popupAriaLabel() {
      return this.showEmojiPicker ? 'Escolher reação' : 'Ações da mensagem';
    },
    closeActionsAriaLabel() {
      return 'Fechar ações da mensagem';
    },
    hasEditChanged() {
      // Aceleres: desabilita "Salvar" enquanto o usuário nao alterar o texto
      // ou digitar algo (evita PATCH sem mudanca).
      const now = (this.editContent || '').trim();
      return now.length > 0 && now !== (this.editOriginalContent || '').trim();
    },
    // Aceleres: dados pro modal "Info da mensagem" (timestamps + status).
    messageInfo() {
      const fmt = ts => {
        if (!ts) return null;
        const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      };
      const attrs = this.contentAttributes || {};
      const status = String(this.message.status || '').toLowerCase();
      const messageType = this.message.message_type ?? this.message.messageType;
      const isIncoming = messageType === 0 || messageType === 'incoming';
      // Recibo por participante (grupo). Sao duas listas disjuntas: quem so
      // recebeu fica em `delivered_receipts`, quem leu fica em
      // `read_receipts`. Mensagens gravadas antes da separacao so tem
      // `read_receipts` — elas continuam sendo lidas aqui como antes.
      const mapReceipts = (list, timeKey) =>
        Array.isArray(list)
          ? list
              .filter(receipt => receipt && receipt.participant_jid)
              .map(receipt => ({
                participantJid: receipt.participant_jid,
                name: receipt.name,
                avatarUrl: receipt.avatar_url,
                at: fmt(receipt[timeKey]) || fmt(receipt.read_at),
              }))
          : [];
      const readReceipts = mapReceipts(attrs.read_receipts, 'read_at');
      const deliveredReceipts = mapReceipts(
        attrs.delivered_receipts,
        'delivered_at'
      );
      const deliveredAt =
        fmt(attrs.delivered_at) || fmt(attrs.external_delivered_at);
      const readAt = fmt(attrs.read_at) || fmt(attrs.external_read_at);
      const sentAt =
        fmt(this.message.created_at) ||
        fmt(attrs.created_at) ||
        fmt(attrs.external_created_at);
      // A cópia que chega ao atendimento já foi entregue ao Zoraxy por
      // definição. A leitura é confirmada somente depois de o atendente abrir
      // a conversa e a WAME aceitar o markRead, cujo horário vem em read_at.
      const delivered =
        isIncoming || ['delivered', 'read'].includes(status) || !!deliveredAt;
      const read = isIncoming
        ? !!readAt
        : status === 'read' || !!readAt || readReceipts.length > 0;
      return {
        sentAt,
        deliveredAt: isIncoming ? sentAt : deliveredAt,
        deliveredDisplay:
          (isIncoming ? sentAt : deliveredAt) ||
          (delivered
            ? 'Entregue (horário indisponível)'
            : 'Ainda não entregue'),
        readAt,
        readDisplay: isIncoming
          ? readAt || 'Ainda não lida no atendimento'
          : readAt || (read ? 'Lida (horário indisponível)' : 'Ainda não lida'),
        hasDelivered: delivered,
        hasRead: read,
        labels: {
          sent: isIncoming ? 'Recebida' : 'Enviada',
          delivered: isIncoming ? 'Recebida no atendimento' : 'Entregue',
          read: isIncoming ? 'Lida no atendimento' : 'Lida',
          readBy: isIncoming ? 'Lida no atendimento' : 'Lido por',
          deliveredTo: isIncoming ? 'Recebida no atendimento' : 'Entregue a',
          emptyContent: 'Mensagem sem conteúdo visível',
        },
        isIncoming,
        isGroup:
          attrs.is_group === true ||
          String(attrs.remote_jid || attrs.wame_remote_jid || '').endsWith(
            '@g.us'
          ) ||
          readReceipts.length > 0 ||
          deliveredReceipts.length > 0,
        readReceipts,
        deliveredReceipts,
      };
    },
    // Aceleres: a citacao no modal de informacoes. `describeQuotedMessage` e o
    // mesmo helper de bubbles/Base.vue e de ReplyToMessage.vue — autor, cor de
    // destaque, rotulo por tipo ("Foto", "GIF", duracao do audio, nome do
    // documento) e miniatura saem todos de la.
    quotedMessageInfo() {
      return this.inReplyTo
        ? describeQuotedMessage(this.inReplyTo, this.$t.bind(this))
        : null;
    },
    // Com texto, o texto fala por si e o icone nao entra; sem texto, o rotulo
    // do anexo vem acompanhado do icone do tipo.
    quotedMediaKindInfo() {
      return this.inReplyTo?.content ? '' : this.quotedMessageInfo?.kind || '';
    },
    quotedPreviewInfo() {
      if (!this.inReplyTo) return '';
      // A figurinha vale pelo desenho; o rotulo dela e "Figurinha", nunca
      // "Foto" — o anexo e um webp/JSON, nao uma foto.
      if (this.quotedMessageInfo?.isSticker)
        return this.quotedMessageInfo.label;

      const { content } = this.inReplyTo;
      if (content) return new MessageFormatter(content).formattedMessage;

      return (
        this.quotedMessageInfo?.label ||
        this.$t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND')
      );
    },
    // Aceleres: mesma leitura que o balao da conversa faz em
    // components-next/message/Message.vue — a figurinha da WAME nao muda o
    // `content_type` da mensagem, quem a declara e `wame_media_type`.
    isStickerMessageInfo() {
      const contentType = String(
        this.message.content_type ?? this.message.contentType ?? ''
      ).toLowerCase();
      const wameMediaType = String(
        this.contentAttributes?.wame_media_type || ''
      ).toLowerCase();

      return contentType === 'sticker' || wameMediaType === 'sticker';
    },
    messageInfoAttachments() {
      const attachments = Array.isArray(this.message.attachments)
        ? this.message.attachments
        : [];
      const isSticker = this.isStickerMessageInfo;

      return attachments.map((attachment, index) => {
        const rawType = String(
          attachment.file_type ||
            attachment.fileType ||
            attachment.content_type ||
            attachment.contentType ||
            attachment.mime_type ||
            attachment.mimeType ||
            ''
        ).toLowerCase();
        const type = rawType.includes('/') ? rawType.split('/')[0] : rawType;
        const url =
          attachment.data_url ||
          attachment.dataUrl ||
          attachment.thumb_url ||
          attachment.thumbUrl ||
          '';
        const mimeType =
          attachment.content_type ||
          attachment.contentType ||
          attachment.mime_type ||
          attachment.mimeType ||
          '';
        const metadataName =
          attachment.file_name ||
          attachment.fileName ||
          attachment.fallback_title ||
          attachment.fallbackTitle ||
          '';
        const name =
          (metadataName
            ? decodeAttachmentName(metadataName)
            : attachmentNameFromUrl(url)) || 'Anexo';
        const metadataExtension =
          attachment.extension || attachment.fileExtension || '';
        const extension = String(
          metadataExtension || name.match(/\.([a-z0-9]+)$/i)?.[1] || ''
        )
          .replace('.', '')
          .toLowerCase();

        return {
          key: attachment.id || `${this.messageId}-${index}`,
          url,
          type,
          mimeType,
          name,
          extension,
          typeLabel:
            extension.toUpperCase() ||
            this.$t('CONVERSATION.UNKNOWN_FILE_TYPE'),
          sizeLabel: formatAttachmentSize(
            attachment.file_size ?? attachment.fileSize
          ),
          isPdf:
            mimeType.toLowerCase() === 'application/pdf' || extension === 'pdf',
          // A vetorial chega como `application/json` e so o player a desenha;
          // a comum e um webp e continua sendo uma imagem — as duas precisam
          // sair do cartao de arquivo e do enquadramento de foto.
          isLottieSticker: isSticker && isLottieStickerAttachment(attachment),
          isImageSticker: isSticker && !isLottieStickerAttachment(attachment),
          // A pergunta vai ao helper com ESTE anexo dentro da mensagem: assim
          // vale tanto o marcador do servidor (`wame_media_type`) quanto a
          // heuristica do anexo (curto e mudo), sem que um album misto
          // contamine um video normal com o GIF do vizinho.
          isGif:
            type === 'video' &&
            isGifMessage({
              content_attributes: this.contentAttributes,
              attachments: [attachment],
            }),
          // Proporcao ja conhecida pelo payload, quando a ponte mandou as
          // dimensoes. Serve para o GIF nascer no tamanho certo; se faltar,
          // `startMessageInfoGif` a preenche com o que o proprio <video>
          // reportar.
          aspectRatio: attachmentAspectRatio(attachment),
          // Aceleres 2026-08-17: a regua da FOTO precisa dos px, nao so da
          // proporcao — o retrato reduz a partir do tamanho do arquivo e a
          // panoramica tem guarda de nitidez. Quando o payload nao traz (o
          // Active Storage ainda nao analisou o blob: conferido nos anexos
          // 33006-33009, sem `width`/`height` no metadata),
          // `measureMessageInfoImage` preenche com o que o proprio <img>
          // reportar.
          naturalWidth: Number(attachment.width) || null,
          naturalHeight: Number(attachment.height) || null,
          // Foto "de verdade": a figurinha tambem chega como imagem e tem
          // enquadramento proprio (quadro de 190px, logo abaixo).
          isPhoto: type === 'image' && !isSticker,
        };
      });
    },
    messageInfoHasSticker() {
      return this.messageInfoAttachments.some(
        attachment =>
          (attachment.isLottieSticker || attachment.isImageSticker) &&
          attachment.url
      );
    },
    /**
     * Largura desenhada da midia com moldura (GIF ou foto), em px.
     *
     * Aceleres 2026-08-17: a bolha e `width: fit-content`, entao com legenda
     * quem media a largura era o TEXTO — e sobrava fundo branco a direita da
     * midia, que e mais estreita. Na conversa isso nao acontece porque a bolha
     * e a midia tem o mesmo teto (420px): o texto nunca passa da imagem.
     *
     * Aqui a largura e calculada, entao ela viaja para o CSS como custom
     * property e o teto da bolha sai dela. O texto passa a quebrar na largura
     * da midia, como no WhatsApp. Num album vale a mais larga: e ela que
     * define ate onde a bolha pode ir.
     */
    messageInfoMediaWidth() {
      const larguras = this.messageInfoAttachments
        .filter(attachment => attachment.url)
        .map(attachment => {
          if (attachment.isGif)
            return this.messageInfoGifBox(attachment)?.width;
          if (attachment.isPhoto)
            return this.messageInfoPhotoBox(attachment)?.width;
          return null;
        })
        .filter(largura => Number.isFinite(largura));

      return larguras.length ? Math.max(...larguras) : null;
    },
    messageInfoBubbleStyle() {
      const largura = this.messageInfoMediaWidth;
      if (!largura) return null;

      return { '--acl-info-media-width': `${largura}px` };
    },
    messageInfoHasAudio() {
      return this.messageInfoAttachments.some(
        attachment => attachment.type === 'audio' && attachment.url
      );
    },
    messageInfoConversation() {
      const getConversationById = this.$store.getters.getConversationById;

      if (typeof getConversationById !== 'function') return {};
      return getConversationById(this.conversationId) || {};
    },
    messageInfoInbox() {
      const inboxId =
        this.message.inbox_id ??
        this.message.inboxId ??
        this.messageInfoConversation.inbox_id ??
        this.messageInfoConversation.inboxId;
      const getInbox = this.$store.getters['inboxes/getInbox'];

      if (!inboxId || typeof getInbox !== 'function') return {};
      return getInbox(inboxId) || {};
    },
    messageInfoAudioSender() {
      const sender = this.message.sender || {
        name: this.message.sender_name || this.message.senderName || '',
      };

      return getAudioAvatarIdentity({
        contentAttributes: this.contentAttributes,
        sender,
        inbox: this.messageInfoInbox,
        conversation: this.messageInfoConversation,
        isOutgoing: this.isOutgoingMessageInfo,
      });
    },
    messageInfoAudioForwarded() {
      return isForwardedAudio(this.contentAttributes);
    },
    messageInfoAudioAppearance() {
      return getAudioAppearance({
        contentAttributes: this.contentAttributes,
        isOutgoing: this.isOutgoingMessageInfo,
      });
    },
    isOutgoingMessageInfo() {
      const type = this.message.message_type ?? this.message.messageType;
      return type === 1 || type === 'outgoing';
    },
    editMenuLabel() {
      return this.isFailedMessage ? 'Editar e reenviar' : 'Editar mensagem';
    },
    editModalTitle() {
      return this.isFailedMessage ? 'Editar e reenviar' : 'Editar mensagem';
    },
    editModalHint() {
      return this.isFailedMessage
        ? 'A mensagem falhou no envio. Ao salvar, ela sera atualizada e reenviada.'
        : 'A edição só funciona em mensagens WhatsApp enviadas há menos de 15 min. Ctrl + Enter para salvar.';
    },
    editSubmitLabel() {
      if (this.editSubmitting) {
        return this.isFailedMessage ? 'Reenviando…' : 'Enviando…';
      }
      return this.isFailedMessage ? 'Salvar e reenviar' : 'Salvar';
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    clampPopupToViewport() {
      const root = this.$refs.aceleresPopupRoot;
      if (!root?.getBoundingClientRect) return;

      const rect = root.getBoundingClientRect();
      const {
        zoom,
        left: viewportLeft,
        top: viewportTop,
        width: viewportWidth,
        height: viewportHeight,
      } = getVisualViewportBounds();
      const popupSize = getUntransformedPopupSize(root, rect);
      const popupWidth = popupSize.width;
      const popupHeight = popupSize.height;
      const padding = 8;
      const minLeft = viewportLeft + padding;
      const minTop = viewportTop + padding;
      const maxRight = viewportLeft + viewportWidth - padding;
      const maxBottom = viewportTop + viewportHeight - padding;

      const mobileMenu = root.querySelector('.aceleres-menu-container');
      if (
        isMobileMessageMenu() &&
        !this.showEmojiPicker &&
        mobileMenu
      ) {
        const mobileActionsTop = getMobileActionsTop({
          focusedCenter: this.resolvedContextMenuPosition.focusedCenter,
          bubbleHeight: this.resolvedContextMenuPosition.bubbleHeight,
          viewportHeight,
          zoom,
        });

        this.resolvedContextMenuPosition = {
          ...this.resolvedContextMenuPosition,
          mobileActionsTop,
        };
        return;
      }

      const anchorLeft = Number(this.resolvedContextMenuPosition.anchorLeft);
      const anchorRight = Number(this.resolvedContextMenuPosition.anchorRight);
      const anchorWidth = Number(this.resolvedContextMenuPosition.anchorWidth);
      const anchorHeight = Number(
        this.resolvedContextMenuPosition.anchorHeight
      );
      const anchorCenter = Number(
        this.resolvedContextMenuPosition.anchorCenter
      );

      // Aceleres 2026-08-18 -- com o seletor de emoji aberto, o eixo VERTICAL
      // passa a medir-se pela BOLHA; com o menu normal continua pelo chevron.
      //
      // O chevron e um botao pequeno colado ao canto de cima da bolha.
      // Enquanto o popup e o menu (~340px) isso nao se nota, mas o "+" troca-o
      // pelo seletor de emoji inteiro (~420px) e a conta `top = ancora -
      // altura` passa a contar a partir daquele cantinho: o seletor sobe muito
      // acima da mensagem e chega a encostar ao topo do ecra. Era este o
      // afastamento relatado — o seletor "longe da mensagem que se tocou".
      // Medido a partir da bolha, a borda do seletor fica colada a mensagem.
      //
      // SO o eixo vertical muda, e de proposito. `anchorRight`/`anchorCenter`
      // decidem o eixo horizontal, e trocá-los pela bolha mudaria o desenho
      // que ja esta acertado: numa bolha larga a recebida (`left-bubble`) abre
      // por `anchorRight + gap`, e com a largura toda da bolha o seletor sairia
      // atirado para a direita, ou fora do ecra. O tamanho do seletor tambem
      // nao muda — o pedido foi aproximar, nao encolher.
      //
      // O `??` NAO pode virar `||`: sao pixeis de viewport, e um zero legitimo
      // (bolha encostada ao topo) cairia de volta para o chevron.
      const usePickerAnchor =
        this.showEmojiPicker &&
        Number.isFinite(Number(this.resolvedContextMenuPosition.bubbleTop));
      const anchorTop = Number(
        usePickerAnchor
          ? (this.resolvedContextMenuPosition.bubbleTop ??
              this.resolvedContextMenuPosition.anchorTop)
          : this.resolvedContextMenuPosition.anchorTop
      );
      const anchorBottom = Number(
        usePickerAnchor
          ? (this.resolvedContextMenuPosition.bubbleBottom ??
              this.resolvedContextMenuPosition.anchorBottom)
          : this.resolvedContextMenuPosition.anchorBottom
      );

      if (Number.isFinite(anchorLeft) && Number.isFinite(anchorTop)) {
        let gap = 4;
        if (this.resolvedContextMenuPosition.pointerAnchor) gap = 0;
        const resolvedAnchorRight = Number.isFinite(anchorRight)
          ? anchorRight
          : anchorLeft + (anchorWidth || 0);
        const resolvedAnchorBottom = Number.isFinite(anchorBottom)
          ? anchorBottom
          : anchorTop + (anchorHeight || 0);
        const spaceAbove = anchorTop - minTop;
        const spaceBelow = maxBottom - resolvedAnchorBottom;
        const fitsAbove = spaceAbove >= popupHeight + gap;
        const fitsBelow = spaceBelow >= popupHeight + gap;
        let direction;
        if (fitsBelow && !fitsAbove) direction = 'down';
        else if (fitsAbove && !fitsBelow) direction = 'up';
        else direction = spaceAbove > spaceBelow ? 'up' : 'down';
        const opensRight =
          this.resolvedContextMenuPosition.horizontalDirection === 'right';
        const desiredLeft = opensRight
          ? resolvedAnchorRight + gap
          : Number.isFinite(anchorCenter)
            ? anchorCenter - popupWidth / 2
            : resolvedAnchorRight - popupWidth;
        const desiredTop =
          direction === 'up'
            ? anchorTop - popupHeight - gap
            : resolvedAnchorBottom + gap;
        const nextLeft = Math.min(
          Math.max(desiredLeft, minLeft),
          Math.max(minLeft, maxRight - popupWidth)
        );
        const nextTop = Math.min(
          Math.max(desiredTop, minTop),
          Math.max(minTop, maxBottom - popupHeight)
        );

        this.resolvedContextMenuPosition = {
          ...this.resolvedContextMenuPosition,
          left: fixedPosition(nextLeft, zoom),
          right: 'auto',
          top: fixedPosition(nextTop, zoom),
          bottom: 'auto',
          direction,
          transformOrigin: `${direction === 'up' ? 'bottom' : 'top'} center`,
        };
        return;
      }

      const nextLeft = Math.min(
        Math.max(rect.left, minLeft),
        Math.max(minLeft, maxRight - popupWidth)
      );
      const nextTop = Math.min(
        Math.max(rect.top, minTop),
        Math.max(minTop, maxBottom - popupHeight)
      );

      if (
        Math.abs(nextLeft - rect.left) < 0.5 &&
        Math.abs(nextTop - rect.top) < 0.5
      ) {
        return;
      }

      this.resolvedContextMenuPosition = {
        ...this.resolvedContextMenuPosition,
        left: fixedPosition(nextLeft, zoom),
        right: 'auto',
        top: fixedPosition(nextTop, zoom),
        bottom: 'auto',
      };
    },
    focusPopupForKeyboard() {
      if (window.matchMedia?.('(pointer: coarse)').matches) return;
      const firstButton = this.$refs.aceleresPopupRoot?.querySelector('button');
      firstButton?.focus?.({ preventScroll: true });
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openEditModal() {
      const raw = this.messageContent || '';
      this.editContent = raw;
      this.editOriginalContent = raw;
      this.editSubmitting = false;
      this.closeEditEmojiPicker();
      this.showEmojiPicker = false;
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.closeEditEmojiPicker();
      this.editSubmitting = false;
    },
    toggleEditEmojiPicker() {
      if (this.showEditEmojiPicker) {
        this.closeEditEmojiPicker();
        return;
      }
      // Aceleres: computa posição fixa baseada no botão de emoji do modal.
      // Teleportamos o picker pro body pra não ser recortado pelo overflow-auto
      // do modal-container. Usa o mesmo tamanho do seletor principal e, em
      // telas pequenas, o próprio componente se ajusta à viewport.
      this.$nextTick(() => {
        const btn = this.$refs.aceleresEditEmojiBtn;
        const PANEL_W = 420;
        const GAP = 8;
        if (btn && btn.getBoundingClientRect) {
          const rect = btn.getBoundingClientRect();
          const maxLeft = window.innerWidth - PANEL_W - 8;
          this.editEmojiPickerStyle = {
            position: 'fixed',
            bottom: `${window.innerHeight - rect.top + GAP}px`,
            left: `${Math.max(8, Math.min(rect.left, maxLeft))}px`,
            zIndex: 10000, // acima do modal (z-9990)
          };
        } else {
          this.editEmojiPickerStyle = {
            position: 'fixed',
            bottom: '25vh',
            left: '50vw',
            transform: 'translateX(-50%)',
            zIndex: 10000,
          };
        }
        this.showEditEmojiPicker = true;
      });
      // Attach outside-click listener in CAPTURE phase — precisa rodar ANTES
      // do handler de mousedown do woot-modal (que fecha o modal em backdrop
      // click). Com stopPropagation aqui, o modal nunca vê o clique, então
      // fecha só o picker.
      document.addEventListener(
        'mousedown',
        this.handleEditEmojiOutsideClick,
        true
      );
    },
    closeEditEmojiPicker() {
      this.showEditEmojiPicker = false;
      this.editEmojiPickerStyle = null;
      document.removeEventListener(
        'mousedown',
        this.handleEditEmojiOutsideClick,
        true
      );
    },
    handleEditEmojiOutsideClick(event) {
      if (!this.showEditEmojiPicker) return;
      const panel = this.$refs.aceleresEditEmojiPanel;
      const btn = this.$refs.aceleresEditEmojiBtn;
      const target = event.target;
      if (panel && panel.contains(target)) return; // clique dentro do picker
      if (btn && btn.contains(target)) return; // clique no próprio botão (toggle cuida)
      this.closeEditEmojiPicker();
      event.stopPropagation();
      event.preventDefault();
    },
    handleEditEmoji(emoji) {
      // Inserção do emoji na posição do cursor (ou no fim como fallback).
      const ta = this.$refs.aceleresEditTextarea;
      if (ta && typeof ta.selectionStart === 'number') {
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const before = this.editContent.slice(0, start);
        const after = this.editContent.slice(end);
        this.editContent = before + emoji + after;
        this.$nextTick(() => {
          const pos = start + emoji.length;
          try {
            ta.setSelectionRange(pos, pos);
            ta.focus();
          } catch (_) {
            /* ignore */
          }
        });
      } else {
        this.editContent = (this.editContent || '') + emoji;
      }
    },
    async confirmEdit() {
      if (this.editSubmitting || !this.hasEditChanged) return;
      this.editSubmitting = true;
      try {
        const updatedMessage = await this.$store.dispatch('editMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content: this.editContent,
        });
        this.showEditModal = false;
        this.closeEditEmojiPicker();
        this.handleClose();

        if (this.isFailedMessage) {
          try {
            await this.$store.dispatch(
              'sendMessageWithData',
              useSnakeCase(updatedMessage)
            );
            useAlert('Mensagem atualizada e reenviada.');
          } catch (retryError) {
            const retryMsg =
              retryError?.response?.data?.error ||
              retryError?.response?.data?.message ||
              (typeof retryError?.message === 'string'
                ? retryError.message
                : '');
            useAlert(retryMsg || 'Mensagem atualizada, mas o reenvio falhou.');
          }
          return;
        }

        useAlert(
          'Mensagem editada. Pode levar alguns segundos até refletir no celular do cliente.'
        );
      } catch (error) {
        // O backend responde com mensagens de erro claras (15 min, deletada, etc)
        const backendMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          (typeof error?.message === 'string' ? error.message : '');
        // Inbox WAME: o `detail` traz o motivo REAL da recusa do WhatsApp
        // ("Message not found", instancia desconectada, mensagem antiga
        // demais). Sem ele o atendente le so "o WhatsApp recusou" e nao sabe
        // se tenta de novo, reconecta ou desiste.
        const backendDetail = error?.response?.data?.detail;
        const fullMsg =
          backendMsg && backendDetail
            ? `${backendMsg} (${backendDetail})`
            : backendMsg || backendDetail;
        useAlert(fullMsg || 'Falha ao editar mensagem.');
      } finally {
        this.editSubmitting = false;
      }
    },
    // Aceleres: Reação de emoji
    async handleReact(emoji) {
      this.showEmojiPicker = false;
      this.handleClose();
      if (
        this.isSelectionMode &&
        isMobileMessageMenu()
      ) {
        this.exitSelectionMode();
      }

      const currentUser = this.$store.getters.getCurrentUser;
      const currentUserId = this.currentUserId || currentUser?.id;
      const agentIdentity = `agent:${currentUserId}`;
      const previousReactions = (
        this.message.content_attributes?.reactions || []
      ).map(reaction => ({ ...reaction }));
      const isCurrentUserReaction = reaction =>
        Number(reaction.user_id ?? reaction.userId) === Number(currentUserId) ||
        (reaction.sender_external_id ?? reaction.senderExternalId) ===
          agentIdentity;
      const previousReaction = previousReactions.find(isCurrentUserReaction);
      // TODO(remocao-de-reacao): REMOVER a propria reacao esta desligado (14/09/2026).
      // A WAME documenta "reacao vazia remove", mas o schema de POST /message/reaction
      // exige `text` com minLength 1 (e o envelope Meta idem): os dois devolvem 400
      // AJV_VALIDATION_ERROR. Com a remocao ligada, a reacao sumia no Zoraxy, voltava
      // com erro e continuava no WhatsApp do cliente. Correcao pedida ao suporte WAME;
      // quando aceitarem reacao vazia, reativar os trechos comentados com esta tag.
      // Mesmo emoji que ja e meu = remocao: nao envia.
      if (previousReaction?.emoji === emoji) return;
      const optimisticReactions = previousReactions.filter(
        reaction => !isCurrentUserReaction(reaction)
      );

      if (previousReaction?.emoji !== emoji) {
        optimisticReactions.push({
          user_id: currentUserId,
          name: currentUser?.name,
          emoji,
          sender_external_id: agentIdentity,
        });
      }

      this.$store.commit('UPDATE_MESSAGE_REACTIONS', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        reactions: optimisticReactions,
      });

      try {
        const { data } = await AceleresMessageApi.react(
          this.currentAccountId,
          this.messageId,
          emoji
        );
        this.$store.commit('UPDATE_MESSAGE_REACTIONS', {
          conversationId: data.conversation_id,
          messageId: data.id,
          reactions: data.content_attributes?.reactions || [],
        });
      } catch (error) {
        this.$store.commit('UPDATE_MESSAGE_REACTIONS', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          reactions: previousReactions,
        });
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível sincronizar a reação com o WhatsApp.'
        );
      }
    },
    toggleEmojiPicker() {
      const shouldOpen = !this.showEmojiPicker;

      // No telemovel o "+" abre a folha grande de emojis (MobileEmojiSheet,
      // dona: ReplyBox). No computador o cartao ancorado continua o certo.
      if (shouldOpen && this.isMobileViewport) {
        this.openMobileReactionSheet();
        return;
      }

      if (shouldOpen) {
        emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
          owner: 'message',
          messageId: this.messageId,
        });
      }
      this.showEmojiPicker = shouldOpen;
    },
    // A reacao volta por `handleReact` deste menu: serve tambem as figurinhas
    // do album, cujo id nao e o de nenhuma bolha Message.vue.
    openMobileReactionSheet() {
      this.handleClose();
      emitter.emit(BUS_EVENTS.OPEN_REACTION_EMOJI_SHEET, {
        messageId: this.messageId,
        onSelect: this.handleReact,
      });
    },
    onEmojiPickerOpened({ owner, messageId } = {}) {
      const isCurrentPicker =
        owner === 'message' && Number(messageId) === Number(this.messageId);
      if (!isCurrentPicker && this.showEmojiPicker) {
        this.handleClose();
      }
    },
    // Aceleres: Reenviar
    handleForward() {
      this.handleClose();
      this.enterSelectionMode();
      this.actionMessages.forEach(message => {
        if (!this.isMessageSelected(message.id)) {
          this.toggleMessageSelection(message);
        }
      });
    },
    openForwardModal() {
      this.handleClose();
      this.showForwardModal = true;
    },
    // Aceleres: Fixar/desafixar
    //
    // Fixar passa antes pelo modal de período, como no WhatsApp. Desafixar não:
    // o endpoint alterna e não há prazo a escolher. Um álbum fixa todas as
    // imagens com o MESMO período — é uma bolha só para o agente, e escolher
    // uma vez por imagem seria absurdo.
    async handlePin() {
      const wasPinned = !!this.enabledOptions.pinned;
      let duration = null;
      if (!wasPinned) {
        duration = await this.$refs.pinDurationDialog.prompt();
        if (!duration) return;
      }
      const togglePayloads = this.actionMessages.map(message => ({
        accountId: this.currentAccountId,
        conversationId: this.conversationId,
        messageId: message.id,
        duration,
      }));
      const results = await Promise.allSettled(
        togglePayloads.map(payload =>
          this.$store.dispatch('pinMessage', payload)
        )
      );
      if (results.some(result => result.status === 'rejected')) {
        useAlert('Não foi possível fixar todas as imagens.');
        this.handleClose();
        return;
      }
      useAlert(
        wasPinned
          ? this.$t('CONVERSATION.CONTEXT_MENU.UNPINNED')
          : this.$t('CONVERSATION.CONTEXT_MENU.PINNED'),
        {
          type: 'button',
          message: 'Desfazer',
          duration: 5000,
          callback: () =>
            Promise.all(
              togglePayloads.map(payload =>
                this.$store.dispatch('pinMessage', payload)
              )
            ),
        }
      );
      this.handleClose();
    },
    // Aceleres: Destacar/remover destaque
    async handleStar() {
      const wasStarred = this.isStarred;
      const togglePayloads = this.actionMessages.map(message => ({
        accountId: this.currentAccountId,
        messageId: message.id,
      }));
      const results = await Promise.allSettled(
        togglePayloads.map(payload =>
          this.$store.dispatch('starMessage', payload)
        )
      );
      if (results.some(result => result.status === 'rejected')) {
        useAlert('Não foi possível destacar todas as imagens.');
        this.handleClose();
        return;
      }
      useAlert(
        wasStarred
          ? this.$t('CONVERSATION.CONTEXT_MENU.UNSTARRED')
          : this.$t('CONVERSATION.CONTEXT_MENU.STARRED'),
        {
          type: 'button',
          message: 'Desfazer',
          duration: 5000,
          callback: () =>
            Promise.all(
              togglePayloads.map(payload =>
                this.$store.dispatch('starMessage', payload)
              )
            ),
        }
      );
      this.handleClose();
    },
    // Aceleres: "Pergunte ao Jarvis" pede uma sugestao de resposta no
    // compositor de baixo (`reply_suggestion` → CopilotEditorSection).
    // Nao abre o painel lateral do Copiloto (`ask_copilot`).
    handleAskJarvis() {
      emitter.emit(CMD_AI_ASSIST, 'reply_suggestion');
      this.handleClose();
    },
    // Aceleres: Adicionar texto à nota privada
    handleAddToNote() {
      emitter.emit(BUS_EVENTS.ADD_MESSAGE_TO_NOTE, {
        content: this.plainTextContent,
        conversationId: this.conversationId,
      });
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.ADDED_TO_NOTE'));
      this.handleClose();
    },
    // Aceleres: Info da mensagem — abre modal com timestamps e status
    handleMessageInfo() {
      this.handleClose();
      this.showMessageInfoModal = true;
      document.addEventListener('keydown', this.handleMessageInfoEscape);
      this.$nextTick(() => this.$refs.messageInfoDrawer?.focus());
      this.messageInfoTextVisibleLength = MESSAGE_INFO_TEXT_TRUNCATE_LENGTH;
      this.loadMessageInfoPdfPreviews();
    },
    showMoreMessageInfoText() {
      this.messageInfoTextVisibleLength += MESSAGE_INFO_TEXT_TRUNCATE_LENGTH;
    },
    closeMessageInfoModal() {
      this.showMessageInfoModal = false;
      document.removeEventListener('keydown', this.handleMessageInfoEscape);
    },
    messageInfoReceiptName(receipt) {
      const participant = (receipt.participantJid || '').split('@')[0];
      return receipt.name || participant || 'Participante';
    },
    messageInfoReceiptInitial(receipt) {
      return this.messageInfoReceiptName(receipt).charAt(0).toUpperCase();
    },
    // Aceleres 2026-08-17: o `muted` precisa estar no ELEMENTO antes do play,
    // senao a politica de autoplay do navegador recusa e o GIF fica parado no
    // primeiro frame. bubbles/Video.vue faz exatamente isto no `loadedmetadata`.
    // Aproveita para guardar a proporcao real do arquivo, que so agora existe.
    startMessageInfoGif(event, attachment) {
      const video = event?.target;
      if (!video) return;

      const width = Number(video.videoWidth);
      const height = Number(video.videoHeight);
      if (width > 0 && height > 0) {
        this.messageInfoGifRatios = {
          ...this.messageInfoGifRatios,
          [attachment.key]: width / height,
        };
      }

      video.muted = true;
      video.loop = true;
      video.play().catch(() => {});
    },
    /**
     * Caixa do GIF no modal: largura desenhada e proporcao.
     *
     * A largura parte da regua da conversa (`gifFrameStyle`, a mesma que
     * bubbles/Video.vue usa) e recebe os dois tetos da gaveta. Sai um NUMERO,
     * e nao uma expressao com percentagem, de proposito: a mesma medida e
     * usada tambem como teto da bolha, e uma percentagem ali seria circular —
     * a bolha mediria o GIF e o GIF mediria a bolha.
     *
     * O teto de ALTURA e aplicado como teto de largura pela propria proporcao:
     * limitar a altura direto brigaria com o `aspect-ratio` e traria de volta a
     * tarja preta e o encolhimento — a mesma armadilha que `gifFrameStyle`
     * documenta.
     */
    messageInfoGifBox(attachment) {
      const ratio =
        this.messageInfoGifRatios[attachment.key] || attachment.aspectRatio;
      const base = ratio ? gifFrameStyle(ratio) : null;
      if (!base) return null;

      return {
        ratio: Number(ratio.toFixed(4)),
        width: Math.round(
          Math.min(
            parseFloat(base.width),
            MESSAGE_INFO_MEDIA_MAX_WIDTH,
            MESSAGE_INFO_MEDIA_MAX_HEIGHT * ratio
          )
        ),
      };
    },
    // Aceleres 2026-08-17: as dimensoes reais da foto, assim que o arquivo
    // carrega. So a partir daqui a regua da conversa consegue decidir o
    // enquadramento — e nos anexos recentes o payload nem sequer traz medida.
    measureMessageInfoImage(event, attachment) {
      const image = event?.target;
      const width = Number(image?.naturalWidth);
      const height = Number(image?.naturalHeight);
      if (!(width > 0 && height > 0)) return;

      this.messageInfoImageSizes = {
        ...this.messageInfoImageSizes,
        [attachment.key]: { width, height },
      };
    },
    /**
     * Caixa da FOTO no modal: a mesma da conversa, reduzida para caber.
     *
     * `photoDisplayBox` (mediaAspectLayout.js) e a regua da conversa em
     * numeros — e quem decide qual formato e RECORTADO (`cover`) e qual e
     * desenhado inteiro. Aqui nada disso e decidido de novo: a caixa so e
     * reduzida por um fator unico ate caber nos tetos da gaveta, e reduzir os
     * dois lados junto e o que mantem o enquadramento igual ao do chat.
     *
     * Era exatamente essa regua que faltava: o modal desenhava `width: 100%`
     * com `max-height` e `contain`, entao uma foto 167x1600 — recorte de
     * 300x344 na conversa — saia como uma tira de 27px de largura.
     */
    messageInfoPhotoBox(attachment) {
      const medida = this.messageInfoImageSizes[attachment.key];
      const width = Number(medida?.width || attachment.naturalWidth);
      const height = Number(medida?.height || attachment.naturalHeight);
      const base = photoDisplayBox(width, height);
      if (!base) return null;

      const fator = Math.min(
        1,
        MESSAGE_INFO_MEDIA_MAX_WIDTH / base.width,
        MESSAGE_INFO_MEDIA_MAX_HEIGHT / base.height
      );

      return {
        width: Math.round(base.width * fator),
        height: Math.round(base.height * fator),
        objectFit: base.objectFit,
      };
    },
    messageInfoPhotoFrameStyle(attachment) {
      const box = this.messageInfoPhotoBox(attachment);
      if (!box) return null;

      return {
        width: `${box.width}px`,
        // A altura vem da PROPORCAO da caixa, nao escrita em px: numa tela
        // estreita a gaveta encolhe, a moldura para nos `max-width: 100%` e a
        // altura acompanha sozinha. Com altura fixa, encolher a largura
        // deformaria o recorte. Mesma armadilha que `gifFrameStyle` documenta.
        aspectRatio: `${box.width} / ${box.height}`,
        maxWidth: '100%',
      };
    },
    messageInfoPhotoAssetStyle(attachment) {
      const box = this.messageInfoPhotoBox(attachment);
      if (!box) return null;

      return {
        width: '100%',
        height: '100%',
        maxHeight: 'none',
        // `cover` so quando a regua da conversa pediu recorte; nos demais
        // formatos a caixa ja tem a proporcao do arquivo e `contain` nao
        // corta nem deixa tarja.
        objectFit: box.objectFit,
      };
    },
    messageInfoGifStyle(attachment) {
      const box = this.messageInfoGifBox(attachment);
      if (!box) return null;

      return {
        width: `${box.width}px`,
        // Rede de seguranca de tela estreita, onde a gaveta ocupa a largura
        // toda: o GIF encolhe junto com a bolha, e a bolha para no mesmo sitio
        // (`min(92%, ...)` no CSS), entao os dois continuam a coincidir.
        maxWidth: '100%',
        aspectRatio: String(box.ratio),
      };
    },
    messageInfoAudioAttachment(attachment) {
      return {
        id: attachment.key,
        dataUrl: attachment.url,
        contentType: attachment.mimeType,
        fileType: 'audio',
      };
    },
    // Aceleres: o host de homolog nao tem poppler/mutool, entao o ActiveStorage
    // devolve thumb_url vazia pra PDF. A miniatura da 1a pagina e renderizada
    // no cliente com pdf.js, igual ao bubble do chat.
    async loadMessageInfoPdfPreviews() {
      const pending = this.messageInfoAttachments.filter(
        attachment =>
          attachment.isPdf &&
          attachment.url &&
          !this.messageInfoPdfPreviews[attachment.key] &&
          !this.messageInfoPdfLoading[attachment.key]
      );

      await Promise.all(
        pending.map(async attachment => {
          this.messageInfoPdfLoading[attachment.key] = true;
          try {
            const result = await createPdfFirstPagePreviewFromUrl(
              attachment.url
            );
            if (result?.preview) {
              this.messageInfoPdfPreviews[attachment.key] = {
                preview: result.preview,
                pageCount: Number(result.pageCount) || 0,
              };
            }
          } catch {
            // A miniatura e enriquecimento: se o PDF nao renderizar, o card
            // com nome, tipo e tamanho continua identificando o anexo.
          } finally {
            delete this.messageInfoPdfLoading[attachment.key];
          }
        })
      );
    },
    messageInfoPdfPreview(attachment) {
      return this.messageInfoPdfPreviews[attachment.key]?.preview || '';
    },
    messageInfoPdfPageLabel(attachment) {
      const pageCount =
        this.messageInfoPdfPreviews[attachment.key]?.pageCount || 0;
      if (!pageCount) return '';
      return pageCount === 1 ? '1 página' : `${pageCount} páginas`;
    },
    isMessageInfoPdfLoading(attachment) {
      return !!this.messageInfoPdfLoading[attachment.key];
    },
    handleMessageInfoEscape(event) {
      if (event.key === 'Escape') this.closeMessageInfoModal();
    },
    // Aceleres: Baixar — no álbum, baixa todos os attachments do grupo.
    async handleDownload() {
      const attachments = this.actionMessages.flatMap(message =>
        Array.isArray(message.attachments) ? message.attachments : []
      );
      if (!attachments.length) {
        useAlert('Nada para baixar');
        this.handleClose();
        return;
      }

      const downloads = await Promise.allSettled(
        attachments.map(async attachment => {
          const url = attachment.data_url || attachment.dataUrl;
          if (!url) throw new Error('Attachment URL unavailable');
          const response = await fetch(url, { credentials: 'omit' });
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return {
            blob: await response.blob(),
            url,
            name:
              attachment.fallback_title ||
              attachment.file_name ||
              decodeURIComponent(url.split('/').pop().split('?')[0]) ||
              'arquivo',
          };
        })
      );

      let downloadedCount = 0;
      downloads.forEach(result => {
        if (result.status === 'rejected') return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(result.value.blob);
        a.download = result.value.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        downloadedCount += 1;
        setTimeout(() => URL.revokeObjectURL(a.href), 1500);
      });

      if (!downloadedCount) useAlert('Arquivo indisponível');
      this.handleClose();
    },
    // Reusa exatamente a barra rapida aberta pela carita da propria mensagem.
    openReactionPicker() {
      if (this.messageInfoInbox.channel_type === 'Channel::Instagram') {
        this.handleReact('❤️');
        return;
      }
      if (this.isMobileViewport) {
        this.openMobileReactionSheet();
        return;
      }
      this.handleClose();
      this.$emit('openQuickReact');
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Período de fixação (Aceleres): abre antes de fixar, nunca ao desafixar. -->
    <PinDurationDialog ref="pinDurationDialog" />
    <!-- Forward Modal (Aceleres) -->
    <ForwardMessageModal
      v-if="showForwardModal"
      :show="showForwardModal"
      :conversation-id="conversationId"
      :selected-messages="actionMessages"
      @cancel="showForwardModal = false"
      @update:show="showForwardModal = $event"
      @success="showForwardModal = false"
    />
    <!-- Info da mensagem (Aceleres): drawer no padrão WhatsApp Web. -->
    <Teleport to="body">
      <Transition name="aceleres-message-info-drawer">
        <div v-if="showMessageInfoModal" class="aceleres-message-info-layer">
          <button
            type="button"
            class="aceleres-message-info-backdrop"
            aria-label="Fechar informações da mensagem"
            @click="closeMessageInfoModal"
          />
          <aside
            ref="messageInfoDrawer"
            class="aceleres-message-info-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="aceleres-message-info-title"
            tabindex="-1"
          >
            <header class="aceleres-message-info-header">
              <button
                type="button"
                class="aceleres-message-info-close"
                aria-label="Fechar"
                @click="closeMessageInfoModal"
              >
                <span class="i-lucide-x" aria-hidden="true" />
              </button>
              <h3 id="aceleres-message-info-title">Informações da mensagem</h3>
            </header>

            <div class="aceleres-message-info-content">
              <section class="aceleres-message-info-preview">
                <div
                  class="aceleres-message-info-bubble"
                  :class="{
                    'is-outgoing': isOutgoingMessageInfo,
                    'is-incoming': !isOutgoingMessageInfo,
                    'has-audio': messageInfoHasAudio,
                    'has-sticker': messageInfoHasSticker,
                    'has-media': !!messageInfoMediaWidth,
                    'has-quote': !!quotedMessageInfo,
                  }"
                  :style="messageInfoBubbleStyle"
                >
                  <!-- Aceleres: quando a mensagem responde outra, o contexto vem
                       junto — mesma marcacao da citacao do balao
                       (components-next/message/bubbles/Base.vue), so que aqui ela
                       e informativa: o modal cobre a lista, entao clicar para
                       rolar ate a original nao teria para onde ir. -->
                  <div
                    v-if="quotedMessageInfo"
                    class="aceleres-message-info-quote"
                    :style="{ borderLeftColor: quotedMessageInfo.accentColor }"
                  >
                    <div class="flex items-start gap-2">
                      <div class="min-w-0 flex-1">
                        <div
                          class="mb-0.5 truncate text-[13px] font-semibold leading-tight"
                          :style="{ color: quotedMessageInfo.accentColor }"
                        >
                          {{ quotedMessageInfo.authorName }}
                        </div>
                        <LottieStickerCanvas
                          v-if="quotedMessageInfo.stickerLottieUrl"
                          :key="quotedMessageInfo.stickerLottieUrl"
                          :url="quotedMessageInfo.stickerLottieUrl"
                          class="size-12"
                        />
                        <img
                          v-else-if="quotedMessageInfo.stickerUrl"
                          :src="quotedMessageInfo.stickerUrl"
                          :alt="quotedMessageInfo.label"
                          class="size-12 object-contain"
                        />
                        <div v-else class="flex min-w-0 items-center gap-1.5">
                          <QuotedMediaIcon
                            v-if="quotedMediaKindInfo"
                            :kind="quotedMediaKindInfo"
                            :icon="quotedMessageInfo.icon"
                          />
                          <div
                            v-dompurify-html="quotedPreviewInfo"
                            v-twemoji
                            class="prose prose-bubble line-clamp-2"
                          />
                        </div>
                      </div>
                      <video
                        v-if="
                          quotedMessageInfo.mediaThumbUrl &&
                          quotedMessageInfo.mediaThumbIsVideo
                        "
                        :src="quotedMessageInfo.mediaThumbUrl"
                        class="size-12 flex-shrink-0 rounded object-cover"
                        muted
                        playsinline
                        preload="metadata"
                        aria-hidden="true"
                      />
                      <img
                        v-else-if="quotedMessageInfo.mediaThumbUrl"
                        :src="quotedMessageInfo.mediaThumbUrl"
                        :alt="quotedMessageInfo.label"
                        class="size-12 flex-shrink-0 rounded object-cover"
                      />
                    </div>
                  </div>
                  <template
                    v-for="attachment in messageInfoAttachments"
                    :key="attachment.key"
                  >
                    <LottieStickerCanvas
                      v-if="attachment.isLottieSticker && attachment.url"
                      :key="attachment.url"
                      :url="attachment.url"
                      class="aceleres-message-info-sticker"
                    />
                    <img
                      v-else-if="attachment.isImageSticker && attachment.url"
                      :src="attachment.url"
                      :alt="attachment.name"
                      class="aceleres-message-info-sticker"
                    />
                    <!-- Aceleres 2026-08-17: a foto tem moldura propria, como
                         na conversa (`acl-image-bubble__asset-frame`). E a
                         moldura que carrega a medida e o recorte; o <img> so
                         preenche. Sem ela a imagem era desenhada com
                         `width: 100%` + `max-height` + `contain`, e uma foto
                         muito vertical — que no chat aparece recortada, "com
                         zoom" — encolhia inteira para caber, virando uma tira
                         fina. -->
                    <span
                      v-else-if="attachment.isPhoto && attachment.url"
                      class="aceleres-message-info-photo"
                      :style="messageInfoPhotoFrameStyle(attachment)"
                    >
                      <img
                        :src="attachment.url"
                        :alt="attachment.name"
                        class="aceleres-message-info-image"
                        :style="messageInfoPhotoAssetStyle(attachment)"
                        @load="measureMessageInfoImage($event, attachment)"
                      />
                    </span>
                    <AudioPresentation
                      v-else-if="attachment.type === 'audio' && attachment.url"
                      class="aceleres-message-info-audio"
                      :attachment="messageInfoAudioAttachment(attachment)"
                      :appearance="messageInfoAudioAppearance"
                      :is-outgoing="isOutgoingMessageInfo"
                      :avatar-name="messageInfoAudioSender.name"
                      :avatar-src="messageInfoAudioSender.src"
                      :forwarded="messageInfoAudioForwarded"
                      :show-message-meta="false"
                      compact
                    />
                    <!-- Aceleres 2026-08-17: GIF nao e video comum. Na
                         conversa (bubbles/Video.vue) ele sai sem controles,
                         mudo e correndo sozinho; aqui caia no `v-else-if` de
                         video e ganhava o player padrao — barra de controles e
                         parado no primeiro frame. A unica diferenca em relacao
                         a bolha e o numero de voltas: la sao 3 e depois o botao
                         "GIF", aqui a animacao corre sem parar, como na
                         galeria. E a mesma razao do pedido de 2026-08-12: as 3
                         voltas existem porque a bolha passa sozinha diante dos
                         olhos ao rolar a conversa; este modal e aberto de
                         proposito, para olhar aquela mensagem. -->
                    <span
                      v-else-if="attachment.isGif && attachment.url"
                      class="aceleres-message-info-gif"
                    >
                      <video
                        class="aceleres-message-info-video is-gif acl-gif-media"
                        :src="attachment.url"
                        :style="messageInfoGifStyle(attachment)"
                        autoplay
                        loop
                        muted
                        playsinline
                        preload="metadata"
                        @loadedmetadata="
                          startMessageInfoGif($event, attachment)
                        "
                      />
                      <span class="aceleres-message-info-gif-label">GIPHY</span>
                    </span>
                    <video
                      v-else-if="attachment.type === 'video' && attachment.url"
                      class="aceleres-message-info-video"
                      controls
                      preload="metadata"
                    >
                      <source
                        :src="attachment.url"
                        :type="attachment.mimeType"
                      />
                    </video>
                    <a
                      v-else
                      :href="attachment.url || undefined"
                      :target="attachment.url ? '_blank' : undefined"
                      :rel="attachment.url ? 'noopener noreferrer' : undefined"
                      class="aceleres-message-info-attachment"
                      :class="{ 'is-unavailable': !attachment.url }"
                      :title="attachment.name"
                      @click.stop
                    >
                      <span
                        v-if="attachment.isPdf && attachment.url"
                        class="aceleres-message-info-attachment-preview"
                        :class="{
                          'is-loading': isMessageInfoPdfLoading(attachment),
                        }"
                      >
                        <img
                          v-if="messageInfoPdfPreview(attachment)"
                          :src="messageInfoPdfPreview(attachment)"
                          alt=""
                        />
                      </span>
                      <span class="aceleres-message-info-attachment-file">
                        <span
                          class="aceleres-message-info-attachment-icon"
                          aria-hidden="true"
                        >
                          <FileIcon :file-type="attachment.extension" />
                        </span>
                        <span class="aceleres-message-info-attachment-details">
                          <span class="aceleres-message-info-attachment-name">
                            {{ attachment.name }}
                          </span>
                          <span class="aceleres-message-info-attachment-meta">
                            <span v-if="messageInfoPdfPageLabel(attachment)">
                              {{ messageInfoPdfPageLabel(attachment) }}
                            </span>
                            <span>{{ attachment.typeLabel }}</span>
                            <span v-if="attachment.sizeLabel">
                              {{ attachment.sizeLabel }}
                            </span>
                          </span>
                        </span>
                      </span>
                    </a>
                  </template>
                  <!-- Aceleres 2026-09-10: a previa desenha o emoji pelo mesmo
                       caminho da bolha da conversa (twemoji + a regra unica de
                       tamanho em aceleres-overrides.css). Sem esta directiva o
                       texto ficava com o emoji do sistema em 14px, e a mesma
                       mensagem aparecia de duas maneiras conforme o sitio onde
                       era lida. `singleMessage` e o que autoriza o tamanho
                       grande quando a mensagem e so emoji, tal como
                       Text/Index.vue faz na conversa. -->
                  <p
                    v-if="plainTextContent"
                    v-twemoji="{ singleMessage: true }"
                    class="aceleres-message-info-text"
                  >
                    {{ messageInfoVisibleText
                    }}<button
                      v-if="messageInfoHasMoreText"
                      type="button"
                      class="aceleres-message-info-read-more"
                      @click.stop="showMoreMessageInfoText"
                    >
                      Ler mais
                    </button>
                  </p>
                  <p
                    v-else-if="!messageInfoAttachments.length"
                    class="aceleres-message-info-text"
                  >
                    {{ messageInfo.labels.emptyContent }}
                  </p>
                  <span class="aceleres-message-info-time">
                    {{ messageInfo.sentAt || 'Horário indisponível' }}
                  </span>
                </div>
              </section>

              <section class="aceleres-message-info-statuses">
                <template v-if="messageInfo.isGroup">
                  <article class="aceleres-message-info-status">
                    <span
                      class="i-lucide-send aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.sent }}</h4>
                      <p>{{ messageInfo.sentAt || 'Horário indisponível' }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{ 'is-pending': !messageInfo.hasDelivered }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>
                        {{
                          messageInfo.deliveredReceipts.length
                            ? messageInfo.labels.deliveredTo
                            : messageInfo.labels.delivered
                        }}
                      </h4>
                      <p v-if="!messageInfo.deliveredReceipts.length">
                        {{ messageInfo.deliveredDisplay }}
                      </p>
                    </div>
                  </article>

                  <article
                    v-for="receipt in messageInfo.deliveredReceipts"
                    :key="`d-${receipt.participantJid}`"
                    class="flex items-center gap-3 border-b border-n-weak py-4 last:border-b-0"
                  >
                    <img
                      v-if="receipt.avatarUrl"
                      :src="receipt.avatarUrl"
                      :alt="messageInfoReceiptName(receipt)"
                      class="size-10 shrink-0 rounded-full object-cover"
                    />
                    <span
                      v-else
                      class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-brand/15 text-sm font-medium text-n-brand"
                      aria-hidden="true"
                    >
                      {{ messageInfoReceiptInitial(receipt) }}
                    </span>
                    <div class="min-w-0">
                      <h4
                        class="m-0 truncate text-base font-medium text-n-slate-12"
                      >
                        {{ messageInfoReceiptName(receipt) }}
                      </h4>
                      <p class="m-0.5 text-sm text-n-slate-10">
                        {{ receipt.at || 'Horário indisponível' }}
                      </p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{
                      'is-read': messageInfo.hasRead,
                      'is-pending': !messageInfo.hasRead,
                    }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.readBy }}</h4>
                      <p v-if="!messageInfo.readReceipts.length">
                        {{ messageInfo.readDisplay }}
                      </p>
                    </div>
                  </article>

                  <article
                    v-for="receipt in messageInfo.readReceipts"
                    :key="receipt.participantJid"
                    class="flex items-center gap-3 border-b border-n-weak py-4 last:border-b-0"
                  >
                    <img
                      v-if="receipt.avatarUrl"
                      :src="receipt.avatarUrl"
                      :alt="messageInfoReceiptName(receipt)"
                      class="size-10 shrink-0 rounded-full object-cover"
                    />
                    <span
                      v-else
                      class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-brand/15 text-sm font-medium text-n-brand"
                      aria-hidden="true"
                    >
                      {{ messageInfoReceiptInitial(receipt) }}
                    </span>
                    <div class="min-w-0">
                      <h4
                        class="m-0 truncate text-base font-medium text-n-slate-12"
                      >
                        {{ messageInfoReceiptName(receipt) }}
                      </h4>
                      <p class="m-0.5 text-sm text-n-slate-10">
                        {{ receipt.at || 'Horário indisponível' }}
                      </p>
                    </div>
                  </article>
                </template>

                <template v-else>
                  <article class="aceleres-message-info-status">
                    <span
                      class="i-lucide-send aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.sent }}</h4>
                      <p>{{ messageInfo.sentAt || 'Horário indisponível' }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{ 'is-pending': !messageInfo.hasDelivered }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.delivered }}</h4>
                      <p>{{ messageInfo.deliveredDisplay }}</p>
                    </div>
                  </article>

                  <article
                    class="aceleres-message-info-status"
                    :class="{
                      'is-read': messageInfo.hasRead,
                      'is-pending': !messageInfo.hasRead,
                    }"
                  >
                    <span
                      class="i-lucide-check-check aceleres-message-info-status-icon"
                      aria-hidden="true"
                    />
                    <div>
                      <h4>{{ messageInfo.labels.read }}</h4>
                      <p>{{ messageInfo.readDisplay }}</p>
                    </div>
                  </article>
                </template>
              </section>
            </div>
          </aside>
        </div>
      </Transition>
    </Teleport>

    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-chevron-down"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <!-- Aceleres: Teleport direto pro body com position:fixed PURA — sem
         clamping de viewport, sem focus-steal, sem scroll-lock. A posição
         vem direto do BCR do chevron calculado em Message.vue. Click fora
         fecha (listener no document). -->
    <Teleport to="body">
      <button
        v-if="isOpen && !isCannedResponseModalOpen"
        type="button"
        class="aceleres-message-focus-backdrop"
        :aria-label="closeActionsAriaLabel"
        @click="handleClose"
      />
      <Transition name="aceleres-message-menu">
        <div
          v-if="isOpen && !isCannedResponseModalOpen"
          ref="aceleresPopupRoot"
          class="aceleres-popup-root"
          :class="{
            'is-emoji-picker': showEmojiPicker,
            'is-mobile-viewport': isMobileViewport,
            'is-incoming':
              resolvedContextMenuPosition.horizontalDirection === 'right',
            'is-outgoing':
              resolvedContextMenuPosition.horizontalDirection === 'left',
            'is-actions-above': resolvedContextMenuPosition.actionsAbove,
          }"
          :style="{
            ...mobileFocusStyle,
            left: resolvedContextMenuPosition.left || 'auto',
            right: resolvedContextMenuPosition.right || 'auto',
            top: resolvedContextMenuPosition.top || 'auto',
            bottom: resolvedContextMenuPosition.bottom || 'auto',
            transformOrigin:
              resolvedContextMenuPosition.transformOrigin || 'top left',
          }"
          role="dialog"
          :aria-label="popupAriaLabel"
          tabindex="-1"
          @click.stop
        >
          <div
            class="aceleres-wa-stack"
            :class="[
              `is-${resolvedContextMenuPosition.direction || 'down'}`,
              { 'is-emoji-picker-open': showEmojiPicker },
            ]"
          >
            <div v-if="showEditModal" class="aceleres-edit-modal">
              <div class="aceleres-edit-modal__header">
                <button
                  type="button"
                  class="aceleres-edit-modal__close"
                  aria-label="Fechar"
                  @click.stop="closeEditModal"
                >
                  <fluent-icon icon="dismiss" size="18" />
                </button>
                <h3 class="aceleres-edit-modal__title">{{ editModalTitle }}</h3>
              </div>

              <div class="aceleres-edit-modal__body">
                <textarea
                  ref="aceleresEditTextarea"
                  v-model="editContent"
                  class="aceleres-edit-modal__textarea"
                  rows="4"
                  placeholder="Digite a nova mensagem…"
                  @keydown.ctrl.enter.prevent="confirmEdit"
                  @keydown.meta.enter.prevent="confirmEdit"
                />
                <p class="aceleres-edit-modal__hint">
                  {{ editModalHint }}
                </p>
              </div>

              <div class="aceleres-edit-modal__footer">
                <div class="aceleres-edit-modal__emoji-wrap">
                  <button
                    ref="aceleresEditEmojiBtn"
                    type="button"
                    class="aceleres-edit-modal__emoji-btn"
                    :class="{ 'is-active': showEditEmojiPicker }"
                    aria-label="Emojis"
                    @click.stop="toggleEditEmojiPicker"
                  >
                    <span
                      class="i-lucide-smile-plus flex-shrink-0"
                      style="width: 20px; height: 20px"
                    />
                  </button>
                  <Teleport to="body">
                    <div
                      v-if="showEditEmojiPicker"
                      ref="aceleresEditEmojiPanel"
                      class="fixed z-[10000]"
                      :style="editEmojiPickerStyle"
                      @click.stop
                      @mousedown.stop
                    >
                      <WhatsAppEmojiPicker :on-click="handleEditEmoji" />
                    </div>
                  </Teleport>
                </div>
                <div class="aceleres-edit-modal__spacer" />
                <button
                  type="button"
                  class="aceleres-edit-modal__cancel"
                  @click.stop="closeEditModal"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="aceleres-edit-modal__send"
                  :disabled="editSubmitting || !hasEditChanged"
                  @click.stop="confirmEdit"
                >
                  <fluent-icon icon="send" size="16" />
                  <span>{{ editSubmitLabel }}</span>
                </button>
              </div>
            </div>
            <div
              v-else-if="!showEmojiPicker"
              class="aceleres-menu-container max-md:select-none max-md:[-webkit-touch-callout:none]"
            >
              <template v-if="isStickerMessageInfo">
                <MenuItem
                  v-if="enabledOptions['replyTo']"
                  :option="{ icon: 'arrow-reply', label: 'Responder' }"
                  variant="icon"
                  @click.stop="handleReplyTo"
                />
                <MenuItem
                  :option="starMenuOption"
                  variant="icon"
                  @click.stop="handleStar"
                />
                <MenuItem
                  :option="pinMenuOption"
                  variant="icon"
                  @click.stop="handlePin"
                />
                <MenuItem
                  v-if="enabledOptions['forward']"
                  :option="{
                    icon: 'i-tabler-corner-up-right-double',
                    label: 'Encaminhar',
                  }"
                  variant="icon"
                  @click.stop="handleForward"
                />
              </template>
              <!-- Ordem WhatsApp: Info, Responder, Copiar, Reagir, Baixar,
             Encaminhar, Fixar, Favoritar, Pergunte ao Jarvis e Adicionar nota.
             Baixar fica entre Reagir e Encaminhar e so aparece em mídia, então
             na mensagem de texto a ordem lida é a pedida, sem buraco.
             enabledOptions decide visibilidade por tipo de mensagem (texto,
             imagem, áudio). Lógica em Message.vue::contextMenuEnabledOptions. -->
              <!-- 1. Info da mensagem -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['info']"
                :option="{ icon: 'info', label: 'Info. da mensagem' }"
                variant="icon"
                @click.stop="handleMessageInfo"
              />
              <!-- 2. Responder -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['replyTo']"
                :option="{ icon: 'arrow-reply', label: 'Responder' }"
                variant="icon"
                @click.stop="handleReplyTo"
              />
              <!-- 3. Copiar -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['copy']"
                :option="{ icon: 'copy', label: 'Copiar' }"
                variant="icon"
                @click.stop="handleCopy"
              />
              <!-- 4. Reagir (so no computador -- ver `isMobileViewport`) -->
              <MenuItem
                v-if="
                  !isStickerMessageInfo &&
                  enabledOptions['react'] &&
                  (!isMobileViewport ||
                    messageInfoInbox.channel_type === 'Channel::Instagram')
                "
                :option="{
                  icon:
                    messageInfoInbox.channel_type === 'Channel::Instagram'
                      ? 'acl-text-heart'
                      : 'emoji-add',
                  label:
                    messageInfoInbox.channel_type === 'Channel::Instagram'
                      ? 'Reagir com coração'
                      : 'Reagir',
                }"
                variant="icon"
                @click.stop="openReactionPicker"
              />
              <!-- 5. Baixar (apenas imagens/áudios/arquivos) -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['download']"
                :option="{ icon: 'arrow-download', label: 'Baixar' }"
                variant="icon"
                @click.stop="handleDownload"
              />
              <!-- 6. Encaminhar -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['forward']"
                :option="{
                  icon: 'i-tabler-corner-up-right-double',
                  label: 'Encaminhar',
                }"
                variant="icon"
                @click.stop="handleForward"
              />

              <!-- 7. Fixar / Desafixar -->
              <MenuItem
                v-if="!isStickerMessageInfo"
                :option="pinMenuOption"
                variant="icon"
                @click.stop="handlePin"
              />
              <!-- 8. Favoritar / Remover dos favoritos -->
              <MenuItem
                v-if="!isStickerMessageInfo"
                :option="starMenuOption"
                variant="icon"
                @click.stop="handleStar"
              />
              <!-- 9. Pergunte ao Jarvis -->
              <MenuItem
                v-if="!isStickerMessageInfo"
                :option="{
                  icon: 'i-lucide-sparkles',
                  label: 'Pergunte ao Jarvis',
                }"
                variant="icon"
                @click.stop="handleAskJarvis"
              />
              <!-- 10. Adicionar nota (só texto, não em mídia/áudio) -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['addToNote']"
                :option="{ icon: 'document', label: 'Adicionar nota' }"
                variant="icon"
                @click.stop="handleAddToNote"
              />

              <!-- 10. Editar mensagem (só outgoing WhatsApp texto até 15 min) -->
              <MenuItem
                v-if="!isStickerMessageInfo && enabledOptions['edit']"
                :option="{ icon: 'edit', label: editMenuLabel }"
                variant="icon"
                @click.stop="openEditModal"
              />
            </div>
            <div
              v-if="
                enabledOptions['react'] &&
                !showEditModal &&
                !showEmojiPicker &&
                !initialShowPicker &&
                messageInfoInbox.channel_type !== 'Channel::Instagram'
              "
              class="aceleres-reaction-pill aceleres-reaction-pill-enter"
              :class="
                resolvedContextMenuPosition.horizontalDirection === 'left'
                  ? 'origin-bottom-right'
                  : 'origin-bottom-left'
              "
            >
              <MessageReactionBar
                :channel-type="messageInfoInbox.channel_type"
                @react="handleReact"
                @more="toggleEmojiPicker"
                @close="handleClose"
              />
            </div>
            <div
              v-if="showEmojiPicker"
              class="aceleres-message-emoji-picker-card"
              :class="{
                'animate-[fade-in-up_0.16s_ease-out_both]': !initialShowPicker,
              }"
            >
              <div class="aceleres-message-emoji-picker-body">
                <WhatsAppEmojiPicker
                  embedded
                  show-recent-reactions
                  :on-select="handleReact"
                  @click.stop
                />
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.aceleres-message-info-layer {
  position: fixed;
  inset: 0;
  z-index: 2147483500;
}

.aceleres-message-info-backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: default;
}

.aceleres-message-info-drawer {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: min(460px, 100%);
  height: 100%;
  overflow: hidden;
  outline: none;
  background: #f0f2f5;
  box-shadow: -10px 0 30px rgba(15, 23, 42, 0.16);
}

.aceleres-message-info-header {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 72px;
  padding: 0 20px;
  gap: 22px;
  background: #f0f2f5;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.aceleres-message-info-header h3 {
  margin: 0;
  color: #111b21;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.2;
}

.aceleres-message-info-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  color: #54656f;
  background: transparent;
  cursor: pointer;
}

.aceleres-message-info-close:hover {
  background: rgba(15, 23, 42, 0.07);
}

.aceleres-message-info-close span {
  width: 22px;
  height: 22px;
}

.aceleres-message-info-content {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  background: #f0f2f5;
}

.aceleres-message-info-preview {
  display: flex;
  align-items: center;
  min-height: 190px;
  padding: 28px 24px;
  background-color: #f5f2eb;
  background-image: url('/assets/images/aceleres-chat-wallpaper-v16.webp');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.aceleres-message-info-bubble {
  position: relative;
  width: fit-content;
  min-width: 170px;
  max-width: 92%;
  padding: 10px 12px 24px;
  border-radius: 9px;
  color: #111b21;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.13);
}

.aceleres-message-info-bubble.is-outgoing {
  margin-left: auto;
  background: #d9fdd3;
}

.aceleres-message-info-bubble.is-incoming {
  margin-right: auto;
}

.aceleres-message-info-bubble.has-audio {
  width: 92%;
  min-width: 0;
  padding: 9px 11px 8px;
}

.aceleres-message-info-text {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.45;
}

/* Aceleres 2026-08-18: mesmo botao do balao da conversa, so que escrito em CSS
   -- o modal e `Teleport to="body"` e segue a convencao de estilo proprio, e as
   utilitarias Tailwind do chat viram uma linha ilegivel aqui. O verde e o mesmo
   (#1b8755) e serve nos dois temas: a bolha da previa nunca escurece. */
.aceleres-message-info-read-more {
  display: inline;
  margin: 0 0 0 -1px;
  padding: 0;
  border: 0;
  color: #1b8755;
  background: transparent;
  font: inherit;
  font-weight: 700;
  line-height: 1;
  /* O paragrafo e `pre-wrap`, entao os espacos que o prettier deixa em volta do
     rotulo apareceriam literais -- inclusive um sobrando depois de "Ler mais".
     Aqui dentro o espaco volta a ser espaco comum: sobra so o separador entre o
     "..." e o botao, como no balao da conversa. */
  white-space: normal;
  text-decoration: none;
  cursor: pointer;
}

.aceleres-message-info-time {
  position: absolute;
  right: 9px;
  bottom: 6px;
  max-width: calc(100% - 18px);
  overflow: hidden;
  color: #667781;
  font-size: 10px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Aceleres 2026-08-17: a moldura e quem tem a medida e quem recorta
   (`overflow: hidden`), repetindo o papel de `.acl-image-bubble__asset-frame`
   na conversa — aquelas regras vivem em aceleres-overrides.css presas a
   `.message-bubble-container` e nao alcancam este modal, que e
   `Teleport to="body"`. Enquanto a foto nao carregou nao ha medida nenhuma, e
   ai vale o `fit-content` com o teto abaixo. */
.aceleres-message-info-photo {
  display: block;
  overflow: hidden;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 8px;
  border-radius: 7px;
}

.aceleres-message-info-image {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
}

/* Aceleres: a figurinha no modal repete o enquadramento da conversa
   (aceleres-overrides.css, `.acl-sticker-bubble > div.relative`): quadro de
   190px sem moldura e sem fundo, valendo tanto para a webp quanto para o
   canvas do Lottie. O modal e teleportado para o `body`, entao aquelas regras
   — presas a `.message-bubble-container` — nao alcancam aqui e a medida
   precisa ser repetida. */
.aceleres-message-info-sticker {
  display: block;
  width: 190px;
  height: 190px;
  max-width: 100%;
  border-radius: 0;
  background: transparent;
  object-fit: contain;
}

.aceleres-message-info-bubble.has-sticker {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 0;
  background: transparent;
  box-shadow: none;
}

/* Igual ao balao da conversa: sem espaco reservado no rodape, o horario sai
   de cima do desenho e vira uma pastilha discreta abaixo dele. */
.aceleres-message-info-bubble.has-sticker .aceleres-message-info-time {
  position: static;
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  margin-top: 10px;
  margin-inline-start: auto;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(45, 130, 65, 0.12);
  font-size: 11px;
}

/* Aceleres: a citacao repete a moldura do balao da conversa
   (bubbles/Base.vue): barra colorida a esquerda, fundo acinzentado e cantos
   arredondados. A cor da barra vem inline, do autor da mensagem citada. O
   modal e teleportado para o `body`, entao a medida precisa morar aqui. */
.aceleres-message-info-quote {
  margin-bottom: 6px;
  border-left: 4px solid transparent;
  border-radius: 6px;
  padding: 6px 8px;
  background: rgba(194, 189, 184, 0.15);
  font-size: 13px;
  line-height: 1.35;
}

/* A bolha de figurinha e transparente e sem padding, o que deixaria a citacao
   solta no ar — respondendo com figurinha a moldura volta, como no WhatsApp.
   O rodape fica curto porque com figurinha o horario ja e estatico. */
.aceleres-message-info-bubble.has-sticker.has-quote {
  padding: 6px 6px 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.13);
}

.aceleres-message-info-bubble.has-sticker.has-quote.is-outgoing {
  background: #d9fdd3;
}

.aceleres-message-info-audio,
.aceleres-message-info-video {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  border-radius: 7px;
}

.aceleres-message-info-audio {
  min-width: 0;
  max-width: 100%;
  margin-bottom: 0;
}

.aceleres-message-info-bubble.has-audio .aceleres-message-info-audio {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

/* Aceleres 2026-08-17: NAO cancelar o transform da coluna aqui. O
   `translate-y-2` de components-next/message/chips/Audio.vue e a compensacao
   dos ~8px que a linha de meta (duracao) rouba do centro da onda: a linha usa
   `align-items: center`, entao quem fica centralizado e a coluna inteira
   (onda + meta), nao a onda. Uma regra `transform: none` vivia exatamente
   neste ponto e era o motivo de a waveform aparecer acima da foto e do botao
   de play so no modal de informacoes. */

/* Pedido 2026-09-15 (facundo): o cartao do audio no painel com as mesmas
   medidas do cartao da conversa (`.acl-wa-audio`, 9/11/8 de padding, em
   public/aceleres-overrides.css). A hora sai da base da bolha e vai para a
   linha da duracao, alinhada ao fim da onda, como no balao: 2px = 67 (cartao)
   - 65,2 (fim da faixa da duracao); 70px = 11 (padding) + 50 (foto) + 9 (gap)
   no recebido, onde a foto fica a direita. O `order` do enviado (foto a
   esquerda) e o microfone vem do overrides, que ja atende este painel. */
.aceleres-message-info-bubble.has-audio .aceleres-message-info-time {
  right: 11px;
  bottom: 2px;
  font-size: 11px;
  line-height: 1.2;
}

.aceleres-message-info-bubble.has-audio.is-incoming
  .aceleres-message-info-time {
  right: 70px;
}

/* A foto e quem define a altura da linha. O `.acl-wa-audio__speed-slot` que a
   substitui no play e fixo (vem do <style scoped> do proprio componente, que
   alcanca o modal); sem igualar a foto, a linha muda de altura no instante do
   play. 64 -> 50 em 02/09, junto com o cartao de 336x67 da conversa. */
.aceleres-message-info-bubble.has-audio :deep(.aceleres-audio-avatar-wrap) {
  width: 50px;
  height: 50px;
  min-width: 50px;
  flex-basis: 50px;
}

.aceleres-message-info-bubble.has-audio
  :deep(.aceleres-audio-avatar-wrap > div:first-child),
.aceleres-message-info-bubble.has-audio
  :deep(
    .aceleres-audio-avatar-wrap > span:first-child:not(.aceleres-audio-mic-icon)
  ) {
  width: 50px;
  height: 50px;
}

.aceleres-message-info-video {
  max-height: 260px;
  background: #111b21;
}

/* Aceleres 2026-08-17: moldura do GIF, repetindo o essencial do
   `.acl-video-bubble__player--gif` da bolha — que nao alcanca aqui porque
   aquelas regras estao presas a `.message-bubble-container` e este modal e
   `Teleport to="body"`. Sem fundo escuro (a animacao enche o quadro) e com a
   marca GIPHY por cima, no mesmo canto e na mesma cor. */
/* Teto da bolha derivado da largura da midia (`--acl-info-media-width`,
   escrita inline por `messageInfoBubbleStyle`). Sem ele, a bolha e
   `fit-content` e a LEGENDA media a largura: um GIF (ou uma foto em pe) de
   180px dentro de uma bolha de 355px deixava fundo branco a direita da
   imagem. Com o teto, o texto quebra na largura da midia — e o `min()` mantem
   a regra de tela estreita, onde os 92% ganham e midia e bolha encolhem
   juntas. */
.aceleres-message-info-bubble.has-media {
  max-width: min(92%, calc(var(--acl-info-media-width) + 24px));
}

/* A moldura abraca o GIF (`fit-content`) em vez de ocupar a bolha inteira: e
   o que mantem a marca GIPHY no canto da animacao, e nao no canto de um
   retangulo maior com espaco vazio ao lado. */
.aceleres-message-info-gif {
  position: relative;
  display: block;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 8px;
}

/* `width: 100%` e `max-height` genericos do video sairiam de cena assim que a
   proporcao for conhecida (o estilo inline de `messageInfoGifStyle` ganha
   deles), mas precisam sair TAMBEM no instante anterior: com a altura cortada
   por `max-height` e a largura em 100%, o `contain` deixava a animacao pequena
   no meio de um quadro largo — que e como o GIF com legenda aparecia. */
.aceleres-message-info-video.is-gif {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: none;
  margin-bottom: 0;
  background: transparent;
  object-fit: contain;
}

.aceleres-message-info-gif-label {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 3;
  color: rgb(var(--slate-11));
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  pointer-events: none;
}

.aceleres-message-info-attachment {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 230px;
  max-width: 100%;
  margin-bottom: 6px;
  padding: 12px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.62);
  color: inherit;
  font-size: 13px;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.aceleres-message-info-attachment.is-unavailable {
  cursor: default;
  opacity: 0.72;
  pointer-events: none;
}

.aceleres-message-info-attachment-preview {
  display: block;
  overflow: hidden;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.aceleres-message-info-attachment-preview.is-loading {
  min-height: 120px;
  background-color: rgba(15, 23, 42, 0.06);
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 30%,
    rgba(255, 255, 255, 0.75) 50%,
    rgba(255, 255, 255, 0) 70%
  );
  background-size: 220% 100%;
  animation: aceleres-message-info-shimmer 1.2s linear infinite;
}

.aceleres-message-info-attachment-preview img {
  display: block;
  width: 100%;
  max-height: 260px;
  object-fit: cover;
  object-position: top center;
}

.aceleres-message-info-attachment-file {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.aceleres-message-info-attachment-icon {
  display: inline-flex;
  width: 28px;
  height: 32px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
}

.aceleres-message-info-attachment-icon > * {
  width: 26px;
  height: 30px;
}

.aceleres-message-info-attachment-details {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.aceleres-message-info-attachment-name {
  font-weight: 500;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.aceleres-message-info-attachment-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(15, 23, 42, 0.55);
  font-size: 11px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.aceleres-message-info-attachment-meta > span + span::before {
  content: '·';
  margin-right: 6px;
}

@keyframes aceleres-message-info-shimmer {
  from {
    background-position: 180% 0;
  }

  to {
    background-position: -80% 0;
  }
}

.aceleres-message-info-statuses {
  margin-top: 10px;
  padding: 8px 26px 24px;
  background: #ffffff;
}

.aceleres-message-info-status {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 12px;
  padding: 21px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.aceleres-message-info-status:last-child {
  border-bottom: 0;
}

.aceleres-message-info-status-icon {
  width: 23px;
  height: 23px;
  margin-top: 1px;
  color: #667781;
}

.aceleres-message-info-status.is-read .aceleres-message-info-status-icon {
  color: #53bdeb;
}

.aceleres-message-info-status.is-pending .aceleres-message-info-status-icon,
.aceleres-message-info-status.is-pending h4 {
  color: #9aa5aa;
}

.aceleres-message-info-status h4 {
  margin: 0;
  color: #111b21;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
}

.aceleres-message-info-status p {
  margin: 4px 0 0;
  color: #667781;
  font-size: 13px;
  line-height: 1.4;
}

.aceleres-message-info-drawer-enter-active,
.aceleres-message-info-drawer-leave-active {
  transition: opacity 0.2s ease;
}

.aceleres-message-info-drawer-enter-active .aceleres-message-info-drawer,
.aceleres-message-info-drawer-leave-active .aceleres-message-info-drawer {
  transition: transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.aceleres-message-info-drawer-enter-from,
.aceleres-message-info-drawer-leave-to {
  opacity: 0;
}

.aceleres-message-info-drawer-enter-from .aceleres-message-info-drawer,
.aceleres-message-info-drawer-leave-to .aceleres-message-info-drawer {
  transform: translateX(100%);
}

:global(.dark) .aceleres-message-info-drawer,
:global(.dark) .aceleres-message-info-header,
:global(.dark) .aceleres-message-info-content {
  background: #111b21;
}

:global(.dark) .aceleres-message-info-header h3,
:global(.dark) .aceleres-message-info-status h4 {
  color: #e9edef;
}

:global(.dark) .aceleres-message-info-statuses {
  background: #202c33;
}

:global(.dark) .aceleres-message-info-close,
:global(.dark) .aceleres-message-info-status p {
  color: #aebac1;
}

@media (max-width: 640px) {
  .aceleres-message-info-drawer {
    width: 100%;
  }
}

/* Aceleres 2026-08-17: o menu conserva o tamanho de toque no celular. No
   computador, o popup fornece medidas compactas por variaveis, sem alterar os
   outros menus que compartilham `MenuItem`. */
.aceleres-menu-container {
  @apply bg-white dark:bg-n-solid-2 overflow-hidden;
  min-width: var(--acl-message-menu-min-width, 240px);
  max-width: var(--acl-message-menu-max-width, 264px);
  // Com 11 itens ampliados o menu passa de 500px. O container e
  // `overflow-hidden`, entao numa janela baixa o "Excluir" seria cortado sem
  // aviso; a rolagem so aparece nesse caso. `clampPopupToViewport` mede o rect
  // ja limitado, entao a ancoragem continua certa.
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  border-radius: var(--acl-message-menu-radius, 12px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.18),
    0 2px 6px rgba(0, 0, 0, 0.06);
  padding: 0;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-0 border-0 border-t border-solid border-n-weak;
    margin: 5px 0;
  }

  // Menu items: compacto nas laterais, legível na vertical.
  :deep(.menu) {
    @apply text-n-slate-12;
    width: 100%;
    min-width: var(--acl-message-menu-min-width, 240px);
    padding: var(--acl-message-menu-item-padding, 10px 15px);
    gap: var(--acl-message-menu-item-gap, 12px);
    border-radius: 0;
    cursor: pointer;
    transition: background-color 0.12s ease;
  }
  :deep(.menu:hover) {
    @apply bg-transparent text-n-slate-12;
    background: rgba(0, 0, 0, 0.04);
    color: inherit;
  }
  :deep(.menu .menu-label) {
    @apply font-normal;
    font-size: var(--acl-message-menu-label-size, 17px);
    line-height: 1.25;
    margin: 0;
  }
  // As duas familias de icones acompanham os tamanhos configurados abaixo.
  :deep(.menu > span[class*='i-']) {
    width: var(--acl-message-menu-class-icon-size, 18px);
    height: var(--acl-message-menu-class-icon-size, 18px);
  }
  :deep(.menu svg) {
    width: var(--acl-message-menu-svg-icon-size, 22px);
    height: var(--acl-message-menu-svg-icon-size, 22px);
  }
}

/* O menu e a barra de reacoes ficam mais compactos apenas no computador. A
   altura minima de 36px dos itens continua garantida pela classe `min-h-9`. */
.aceleres-popup-root:not(.is-mobile-viewport) {
  --acl-message-menu-min-width: 216px;
  --acl-message-menu-max-width: 236px;
  --acl-message-menu-radius: 10px;
  --acl-message-menu-item-padding: 6px 12px;
  --acl-message-menu-item-gap: 8px;
  --acl-message-menu-label-size: 14px;
  --acl-message-menu-class-icon-size: 16px;
  --acl-message-menu-svg-icon-size: 16px;
  --acl-message-stack-gap: 3px;
  --acl-message-reaction-pill-padding: 2px 4px;
  --acl-message-reaction-item-gap: 1px;
}

:deep(.aceleres-menu-danger .menu) {
  color: rgb(220, 38, 38);
}
:deep(.aceleres-menu-danger .menu:hover) {
  background: rgba(220, 38, 38, 0.08) !important;
  color: rgb(220, 38, 38) !important;
}

.aceleres-reaction-bar {
  @apply bg-transparent;
}

/* Raiz teleportada para o body, ancorada no clique e corrigida depois da
   renderização para nunca escapar da visual viewport. */
/* Aceleres 2026-08-17: acima do compositor. O shell do compositor
   (MessagesView.vue) e um contexto de empilhamento com z-index 2147482000 e
   `isolation: isolate`, entao qualquer menu na casa dos milhares — mesmo
   teleportado para o body — era pintado ATRAS dele e ficava escondido ou
   inclicavel na parte de baixo da conversa.

   2147482700 nao e um numero novo: e a mesma faixa que os menus flutuantes do
   mobile ja usam (aceleres-overrides.css), acima do compositor e abaixo do
   aviso de "digitando" (2147483000) e dos modais (2147483500+). */
.aceleres-popup-root {
  position: fixed;
  z-index: 2147482700;
  outline: none;
  will-change: transform, opacity;
}

.aceleres-message-focus-backdrop {
  display: none;
}
.aceleres-message-menu-enter-active,
.aceleres-message-menu-leave-active {
  transition:
    opacity 0.13s ease,
    transform 0.16s cubic-bezier(0.16, 1, 0.3, 1);
}
.aceleres-message-menu-enter-from,
.aceleres-message-menu-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(3px);
}
@keyframes aceleres-popup-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Aceleres: a posição da barra segue a metade da conversa em que está a
   seta. Seta embaixo: reações acima e ações junto à mensagem. Seta em cima:
   ações junto à mensagem e reações abaixo. */
.aceleres-wa-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--acl-message-stack-gap, 4px);
}
.aceleres-wa-stack.is-up {
  flex-direction: column-reverse;
}
.aceleres-wa-stack.is-emoji-picker-open {
  flex-direction: column;
}
.aceleres-wa-stack.is-up.is-emoji-picker-open {
  flex-direction: column-reverse;
}
.aceleres-reaction-pill {
  background: #fff;
  border-radius: 9999px;
  padding: var(--acl-message-reaction-pill-padding, 2px 7px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.12),
    0 2px 5px rgba(0, 0, 0, 0.1);
  display: inline-flex;
}
/* A barra de reações cresce a partir do próprio centro. */
.aceleres-reaction-pill-enter {
  animation: aceleres-reaction-pill-enter 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes aceleres-reaction-pill-enter {
  from {
    opacity: 0.65;
    transform: scale(0.65);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.aceleres-reaction-pill :deep(.aceleres-reaction-bar) {
  animation: none;
  clip-path: none;
}
.aceleres-reaction-pill :deep(.aceleres-reaction-bar),
.aceleres-reaction-pill :deep(.aceleres-reaction-bar > div) {
  gap: var(--acl-message-reaction-item-gap, 2px);
}
.aceleres-reaction-pill :deep(.aceleres-emoji-btn) {
  width: var(--acl-message-reaction-button-size, 34px) !important;
  height: var(--acl-message-reaction-button-size, 34px) !important;
  font-size: var(--acl-message-reaction-font-size, 22px);
}
.aceleres-reaction-pill :deep(.aceleres-emoji-btn span) {
  width: var(--acl-message-reaction-glyph-size, 23px) !important;
  height: var(--acl-message-reaction-glyph-size, 23px) !important;
  font-size: var(--acl-message-reaction-glyph-size, 23px) !important;
}
.aceleres-reaction-pill
  :deep(.aceleres-emoji-btn:not(.aceleres-emoji-btn--more) .cw-emoji) {
  display: block !important;
  width: var(--acl-message-reaction-glyph-size, 23px) !important;
  height: var(--acl-message-reaction-glyph-size, 23px) !important;
}
.aceleres-reaction-pill
  :deep(.aceleres-emoji-btn--more .i-lucide-plus) {
  width: var(--acl-message-reaction-more-icon-size, 16px);
  height: var(--acl-message-reaction-more-icon-size, 16px);
}
.aceleres-message-emoji-picker-card {
  @apply dark:bg-n-solid-2;
  background: #f7f8fa;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.06);
  width: 390px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.aceleres-message-emoji-picker-body {
  /* Wrapper do EmojiInput. Esse componente foi feito pra abrir flutuando em
     cima de um input box (position:absolute, top:-95px, w-80, etc). Aqui ele
     vai EMBUTIDO no nosso popup, então normalizamos tudo via :deep.
     overflow:hidden mata qualquer scroll horizontal residual; a área de
     emojis cuida do seu próprio scroll vertical. */
  position: relative;
  width: 100%;
  overflow: hidden;

  :deep(.emoji-dialog) {
    position: static !important;
    width: 100% !important;
    height: auto !important;
    max-height: none !important;
    top: auto !important;
    right: auto !important;
    left: auto !important;
    box-shadow: none !important;
    outline: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-sizing: border-box !important;
    background: transparent !important;
    z-index: auto !important;
    overflow: hidden !important;
  }
  /* Aceleres: reordena o flex interno do EmojiInput pra:
       1. footer (tabs por categoria + clock de recentes) no TOPO
       2. search row no meio
       3. grid de emojis embaixo
     CSS-only — DOM continua igual, só mexemos no order. (2026-07-01) */
  :deep(.emoji-dialog > .flex.flex-col) {
    width: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden;
    display: flex !important;
    flex-direction: column;
  }
  :deep(.emoji-dialog > .flex.flex-col > .emoji-dialog--footer) {
    order: -1;
  }
  /* A setinha pseudo-elemento apontava pra um botão fora; aqui não faz
     sentido — esconde. */
  :deep(.emoji-dialog::before) {
    display: none !important;
  }
  /* Search box ocupa toda largura, padding mais respirado. */
  :deep(.emoji-dialog > .flex > .flex.gap-2) {
    margin: 10px 12px 6px 12px !important;
    z-index: 20 !important;
    background: #f7f8fa !important;
  }
  /* Aceleres: input de busca em formato PÍLULA (border-radius 50px) com
     lupa SVG embutida como background-image no canto esquerdo — mimica
     o estilo de busca do WhatsApp Web. (pedido 2026-07-01) */
  :deep(.emoji-dialog input[type='text']) {
    width: 100%;
    border-radius: 50px !important;
    padding-left: 32px !important;
    padding-right: 12px !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 10px center;
    background-size: 14px 14px;
  }
  /* Grid de emojis: rola na vertical, NUNCA horizontal. Altura fixa pra
     evitar que o picker estique sem fim. */
  :deep(.emoji-item) {
    height: 220px !important;
    max-height: 220px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding: 0 8px 8px 8px;
    box-sizing: border-box !important;
    overscroll-behavior: contain;
    /* scroll suave + barra fininha em browsers WebKit */
    scrollbar-width: thin;
  }
  /* Continuous mode (scroll-sync-tabs) — section title vira sticky pra
     ficar visível enquanto rola a categoria, igual WhatsApp. */
  :deep(.emoji-item--continuous .emoji--section-title) {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f7f8fa;
    padding: 6px 4px !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.55) !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  :deep(.emoji-item::-webkit-scrollbar) {
    width: 8px;
  }
  :deep(.emoji-item::-webkit-scrollbar-thumb) {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }
  :deep(.emoji-item::-webkit-scrollbar-thumb:hover) {
    background: rgba(0, 0, 0, 0.25);
  }
  /* Aceleres: grid de emojis vira CSS Grid de 8 colunas fixas (1fr cada)
     em vez de flex-wrap. flex-wrap deixava a última coluna vazia porque
     a soma dos items (34px * 8 + gap) não batia com a largura interna e
     o navegador arredondava pra 7 items por linha. Com grid 8x1fr, cada
     célula fica com 1/8 da largura útil — a 8ª coluna PREENCHE. Também
     aumenta um pouco o tamanho do emoji dentro (font-size + height).
     (pedido 2026-07-01) */
  :deep(.emoji--row) {
    display: grid !important;
    grid-template-columns: repeat(8, 1fr);
    gap: 2px;
    padding: 0 !important;
  }
  :deep(.emoji--row .emoji--item) {
    width: 100% !important;
    height: 38px !important;
    font-size: 24px !important;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: background 0.1s;
    /* Bandeiras e emojis "wide" não devem vazar pra fora do quadrado. */
    flex-shrink: 0;
    overflow: hidden;
  }
  /* Footer (tabs por categoria) — reposicionado no TOPO via `order: -1`
     acima. Sem cor de fundo, sem borda, padding vertical maior. Padding
     horizontal maior também pra ícones não colarem nas laterais.
     (pedido 2026-07-01) */
  :deep(.emoji-dialog--footer) {
    background: #f7f8fa !important;
    border-bottom: 0 !important;
    z-index: 30 !important;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 10px 14px !important;
  }
  /* Aceleres: o primeiro tab representa o histórico de reações recentes.
     Esconde o ícone original e mantém o relógio via máscara, herdando a cor
     do botão para que os estados ativo e hover continuem funcionando. */
  :deep(.emoji-dialog--footer li:first-child .emoji--item) {
    position: relative;
  }
  :deep(.emoji-dialog--footer li:first-child .emoji--item svg),
  :deep(.emoji-dialog--footer li:first-child .emoji--item i) {
    display: none !important;
  }
  :deep(.emoji-dialog--footer li:first-child .emoji--item)::before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: currentColor;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpolyline points='12 6 12 12 16 14'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpolyline points='12 6 12 12 16 14'/%3E%3C/svg%3E");
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
  }
  :deep(.emoji-dialog--footer ul) {
    gap: 0 !important;
    justify-content: space-between;
    overflow: hidden !important;
    left: 0 !important;
    padding: 2px 0 !important;
  }
  :deep(.emoji-dialog--footer li) {
    flex: 1 1 0;
    position: relative;
    padding: 2px !important;
  }
  :deep(.emoji-dialog--footer li .emoji--item) {
    width: 100% !important;
    height: 34px !important;
    font-size: 22px;
    color: rgba(0, 0, 0, 0.5);
    transition:
      color 0.12s,
      opacity 0.12s;
    opacity: 0.65;
    filter: grayscale(0.15);
  }
  :deep(.emoji-dialog--footer li:hover .emoji--item) {
    opacity: 1;
    filter: none;
  }
  :deep(.emoji-dialog--footer li .active) {
    background: transparent !important;
    color: rgba(0, 0, 0, 0.95);
    font-weight: 700;
    opacity: 1 !important;
    filter: none !important;
  }
}

@media (max-width: 767px) and (pointer: coarse) {
  .aceleres-message-focus-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 2147482500;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(7px);
    -webkit-backdrop-filter: blur(7px);
  }

  .aceleres-popup-root:not(.is-emoji-picker) {
    inset: 0 !important;
    width: 100%;
    height: 100%;
    transform: none !important;
    pointer-events: none;
  }

  .aceleres-popup-root:not(.is-emoji-picker) .aceleres-wa-stack {
    position: static;
    display: block;
  }

  .aceleres-popup-root:not(.is-emoji-picker) .aceleres-reaction-pill {
    position: absolute;
    box-sizing: border-box;
    max-width: calc(100vw - 24px);
    top: calc(
      var(--aceleres-focused-center-y) - var(
          --aceleres-focused-bubble-height,
          72px
        ) / 2 - 10px
    );
    bottom: auto;
    transform: translateY(-100%);
    pointer-events: auto;
    /* A pilula cresce no mesmo ponto em que permanece aberta. */
    padding: 4px 10px;
    transform-origin: center center;
    animation: aceleres-mobile-reaction-pill-in 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @keyframes aceleres-mobile-reaction-pill-in {
    0% {
      opacity: 0.65;
      transform: translateY(-100%) scale(0.65);
    }
    100% {
      opacity: 1;
      transform: translateY(-100%) scale(1);
    }
  }

  .aceleres-popup-root:not(.is-emoji-picker) .aceleres-menu-container {
    position: absolute;
    top: var(
      --aceleres-mobile-actions-top,
      calc(
        var(--aceleres-focused-center-y) +
          var(--aceleres-focused-bubble-height, 72px) / 2 + 10px
      )
    );
    max-height: calc(100dvh - var(--aceleres-mobile-actions-top, 50vh) - 12px);
    overflow-y: auto;
    overscroll-behavior: contain;
    pointer-events: auto;
  }

  .aceleres-popup-root:not(.is-emoji-picker) .aceleres-reaction-pill
    :deep(.aceleres-reaction-bar),
  .aceleres-popup-root:not(.is-emoji-picker) .aceleres-reaction-pill
    :deep(.aceleres-reaction-bar > div) {
    min-width: 0;
  }

  .aceleres-popup-root.is-incoming:not(.is-emoji-picker)
    .aceleres-reaction-pill {
    right: auto;
    left: 12px;
    transform: translateY(-100%);
  }

  .aceleres-popup-root.is-outgoing:not(.is-emoji-picker)
    .aceleres-reaction-pill {
    right: 12px;
    left: auto;
    transform: translateY(-100%);
  }

  .aceleres-popup-root.is-incoming:not(.is-emoji-picker)
    .aceleres-menu-container {
    right: auto;
    left: var(--aceleres-focused-left, 12px);
    transform: none;
  }

  .aceleres-popup-root.is-outgoing:not(.is-emoji-picker)
    .aceleres-menu-container {
    right: var(--aceleres-focused-right, 12px);
    left: auto;
    transform: none;
  }

  .aceleres-menu-container {
    min-width: 268px;
    max-width: min(312px, calc(100vw - 24px));
    border-radius: 14px;
  }

  .aceleres-menu-container :deep(.menu) {
    min-width: 268px;
    min-height: 40px;
    padding: 9px 17px;
  }

  .aceleres-reaction-pill :deep(.aceleres-emoji-btn) {
    width: min(42px, calc((100vw - 64px) / 8)) !important;
    height: min(42px, calc((100vw - 64px) / 8)) !important;
    font-size: 25px;
  }
}

/* Aceleres: a largura visual pode nao coincidir com a media query quando o
   navegador esta ampliado. A classe vem de `isMobileMessageMenu`, entao a folha
   continua correta nesses casos sem duplicar a regra responsiva. */
.aceleres-popup-root.is-mobile-viewport.is-emoji-picker {
  right: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  top: auto !important;
  width: auto;
  transform-origin: bottom center !important;
}

.aceleres-popup-root.is-mobile-viewport.is-emoji-picker .aceleres-wa-stack,
.aceleres-popup-root.is-mobile-viewport.is-emoji-picker
  .aceleres-message-emoji-picker-card {
  width: 100%;
  max-width: none;
}

.aceleres-popup-root.is-mobile-viewport.is-emoji-picker
  .aceleres-message-emoji-picker-card {
  /* Encostada em baixo, so as quinas de cima e que arredondam. A borda
     inferior sai: nao ha nada por baixo dela para separar. */
  border-radius: 18px 18px 0 0;
  border-bottom: 0;
  padding-bottom: env(safe-area-inset-bottom);
}

.aceleres-popup-root.is-mobile-viewport.is-emoji-picker
  .aceleres-message-emoji-picker-body
  :deep(.emoji-item) {
  height: min(42dvh, 320px) !important;
  max-height: min(42dvh, 320px) !important;
}

@media (max-width: 767px) and (pointer: coarse) and (max-height: 650px) {
  .aceleres-popup-root:not(.is-emoji-picker) .aceleres-menu-container {
    width: calc(100vw - 24px);
    right: 12px;
    left: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aceleres-message-menu-enter-active,
  .aceleres-message-menu-leave-active,
  .aceleres-reaction-pill :deep(.aceleres-emoji-btn) {
    transition-duration: 0.01ms !important;
  }
}

/* Aceleres: modal de editar mensagem — estilo WhatsApp Web.
   X esquerda no header, textarea grande, footer com emoji + salvar. */
.aceleres-edit-popup-root {
  animation: aceleres-popup-in 0.12s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.aceleres-edit-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  max-width: none;
  max-height: 100%;
  background: var(--n-alpha-1, #fff);
  color: var(--s-900, #1f2933);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.22),
    0 2px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  @apply dark:bg-n-solid-2 dark:border-n-alpha-2;
}

.aceleres-edit-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--s-100, #e2e8f0);
}
.aceleres-edit-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.aceleres-edit-modal__close:hover {
  background: var(--s-100, #e2e8f0);
}
.aceleres-edit-modal__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.aceleres-edit-modal__body {
  padding: 16px;
  overflow: auto;
}
.aceleres-edit-modal__textarea {
  width: 100%;
  border: 1px solid var(--s-200, #cbd5e0);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.4;
  resize: vertical;
  min-height: 96px;
  background: var(--n-alpha-1, #fff);
  color: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.aceleres-edit-modal__textarea:focus {
  outline: none;
  border-color: #00a884;
  box-shadow: 0 0 0 2px rgba(0, 168, 132, 0.18);
}
.aceleres-edit-modal__hint {
  margin: 8px 2px 0;
  font-size: 12px;
  color: var(--s-500, #718096);
}

.aceleres-edit-modal__footer {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 14px;
  border-top: 1px solid var(--s-100, #e2e8f0);
}
.aceleres-edit-modal__spacer {
  flex: 1;
}

.aceleres-edit-modal__emoji-wrap {
  position: relative;
}
.aceleres-edit-modal__emoji-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--s-500, #718096);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}
.aceleres-edit-modal__emoji-btn:hover,
.aceleres-edit-modal__emoji-btn.is-active {
  background: var(--s-100, #e2e8f0);
  color: #00a884;
}
.aceleres-edit-modal__emoji-panel {
  /* Placeholder — não uso mais aqui, mantido só pra evitar quebrar CSS antigo. */
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 20;
}

/* Aceleres: card do picker de emoji do modal de editar. Compacto (300px),
   Teleportado pro body via .aceleres-edit-modal__emoji-panel-floating. Não
   herda do .aceleres-emoji-picker-card (que é 420px, tampava textarea). */
.aceleres-edit-modal__emoji-panel-floating {
  width: 300px;
  max-width: calc(100vw - 16px);
  max-height: 340px;
  background: #f7f8fa;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.22),
    0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @apply dark:bg-n-solid-2 dark:border-n-alpha-2;
}
.aceleres-edit-modal__emoji-picker-body {
  /* Deep overrides já vêm de .aceleres-emoji-picker-body (compartilhada com
     Reagir/ReplyBox). Aqui só garantimos que o grid tenha scroll interno
     dentro do max-height do card. */
  flex: 1 1 auto;
  min-height: 0;
}
.aceleres-edit-modal__emoji-picker-body :deep(.emoji-dialog) {
  height: 100% !important;
  max-height: 100% !important;
}
.aceleres-edit-modal__emoji-picker-body :deep(.emoji-dialog > .flex.flex-col) {
  height: 100% !important;
}
/* Em telas estreitas, deixa o picker um pouco menor pra sobrar respiro. */
@media (max-width: 480px) {
  .aceleres-edit-modal__emoji-panel-floating {
    width: 92vw;
    max-height: 60vh;
  }
}

.aceleres-edit-modal__cancel {
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: var(--s-700, #4a5568);
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.aceleres-edit-modal__cancel:hover {
  background: var(--s-100, #e2e8f0);
}

.aceleres-edit-modal__send {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  background: #00a884;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease;
}
.aceleres-edit-modal__send:hover:not(:disabled) {
  background: #069373;
}
.aceleres-edit-modal__send:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
